import { handleNativeAgentRequest } from './hosts/nativeAgent';
import { updateAgentPointSizes } from './hosts/agentPointSizing';
import { renderAgentComparison } from './hosts/agentComparison';
import { createModelObject, updateModelPlayback } from './models/sceneModel';
import { handleSceneModelMessage } from './models/modelMessages';
import * as THREE from 'three';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CustomArcballControls, VirtualBallControls } from './controls';
import { initializeThemes, getThemeByName, applyTheme, getCurrentThemeName } from './themes';
import { RotationCenterManager } from './RotationCenterManager';
import { MeasurementManager } from './MeasurementManager';
import { measurementState } from './state/measurement.svelte';
import { FilmManager } from './film/FilmManager';
import { mountFilmPanel } from './filmPanelMount';
import { mountMeasurementQuickActions } from './measurementQuickActionsMount';
import { mountSmallViewAffordance } from './smallViewAffordanceMount';
import { mountFileActivityIndicator } from './fileActivityIndicatorMount';
import { SmallViewAffordance } from './smallViewAffordance';
import { ScreenAnchorController } from './screenAnchor';
import { TouchNavigationController } from './touchNavigation';
import { SelectionManager, } from './SelectionManager';
// Environment detection - works in both VSCode and browser
const isVSCode = typeof acquireVsCodeApi !== 'undefined';
const BACKGROUND_CHANGE_MESSAGES = new Set([
    'stonexColorReady',
    'spatialData',
    'multiSpatialData',
    'sceneModelData',
    'splatContainerUri',
    'ultimateRawBinaryData',
    'ultimateRawBinaryUri',
    'directTypedArrayData',
    'binarySpatialData',
    'addFiles',
    'startLargeFile',
    'largeFileChunk',
    'largeFileComplete',
    'depthData',
    'objData',
    'stlData',
    'xyzData',
    'pcdData',
    'ptsData',
    'kittiBinData',
    'offData',
    'gltfData',
    'volumeData',
    'colmapModelFiles',
    'colmapImages',
    'npyData',
    'xyzVariantData',
    'poseData',
]);
// Shared file handling functionality
import { DEFAULT_COLORS, } from './fileHandler';
// Depth processing modules
import { ColorImageLoader } from './colorImageLoader';
import { PerfTimer, setPerfSink } from './utils/perfLog';
import { messageBytes } from './utils/messageBytes';
import * as sequencePlayback from './sequencePlayback';
import * as pose from './pose';
import * as renderStats from './renderStats';
import * as pointCloudRenderer from './visualization/PointCloudRenderer';
import { boundsAreOutlierDominated, robustPointBounds } from './visualization/robustBounds';
import { DEFAULT_POINT_SIZE } from './visualization/PointCloudRenderer';
import * as containerPerf from './utils/containerPerf';
import * as meshBuilder from './visualization/MeshBuilder';
import * as stonexCameras from './visualization/stonexCameras';
import { addE57CameraVisualization } from './visualization/e57Cameras';
import { addColmapCameraVisualization } from './visualization/colmapCameras';
import { buildSparseCloud, collectColmapModelFiles, colmapReconstructionName, parseColmapModel, } from './formats/colmap/colmapFiles';
import { loadColmapTextures } from './formats/colmap/colmapTextures';
import { attachColmapFrameImage } from './formats/colmap/colmapReconstruction';
import * as uiStatus from './ui/status';
import * as intensity from './utils/intensity';
import * as commentSettings from './depth/commentSettings';
import * as cameraProfile from './cameraProfile';
import * as renderModeToggles from './renderModeToggles';
import { SplatModeManager, handleSplatContainerUri } from './visualization/splatMode';
import * as colorModeUtils from './colorMode';
import * as pointSizeScaling from './pointSizeScaling';
import * as depthPanelState from './depth/panelState';
import * as sceneBrightness from './sceneBrightness';
import * as depthDefaultSettings from './depth/defaultSettings';
import * as calibrationForm from './depth/calibrationForm';
import * as controlSchemeSwitcher from './controlSchemeSwitcher';
import * as cameraConvention from './cameraConvention';
import * as edl from './edl';
import * as transparency from './transparency';
import * as plyExport from './plyExport';
import * as colorModeModule from './colorMode';
import * as registrationFeature from './registrationFeature';
import { configureRegistrationExtensionHost, handleRegistrationExtensionResult, } from './registration';
import * as stationPipelineFeature from './stationPipelineFeature';
import * as rotationCenterFeature from './rotationCenterFeature';
import { applyScannerStartView, applyZUpOrientation, scannerCapturePointFor, shouldApplySavedViewConvention, shouldOrientZUp, } from './cameraOrientation';
import { applyFixedClipPlanes, FIXED_CAMERA_FAR, FIXED_CAMERA_NEAR } from './cameraClipping';
import * as axesFeature from './axesFeature';
import * as transformationMatrix from './transformationMatrix';
import * as depthCameraParamsPrompt from './depthCameraParamsPrompt';
import * as formatDataHandlers from './formatDataHandlers';
import * as transformDialogs from './transformDialogs';
import * as browserFileDragDrop from './browserFileDragDrop';
import * as liveDepthUpdate from './depth/liveDepthUpdate';
import * as normalsVisualizer from './normalsVisualizer';
import * as depthConversionPipeline from './depth/depthConversionPipeline';
import * as colorImageForDepth from './depth/colorImageForDepth';
import * as largeFileChunking from './largeFileChunking';
import * as binaryDataHandlers from './binaryDataHandlers';
import * as datasetWorkflow from './depth/datasetWorkflow';
import { mountSvelteSmokeTest } from './svelteSmokeTestMount';
import { mountErrorOverlay } from './errorOverlayMount';
import { mountLoadingOverlay } from './loadingOverlayMount';
import { mountTabNav } from './tabNavMount';
import { mountWelcomeMessage } from './welcomeMessageMount';
import { installBrowserAgent } from './hosts/browserAgent';
import { mountPerformanceStats } from './performanceStatsMount';
import { mountSequenceControls } from './sequenceControlsMount';
import { mountFileList } from './fileListMount';
import { mountGlobalAlignMenu } from './globalAlignMenuMount';
import { runBenchmarkScenario } from './benchmarkScenario';
import { mountStats } from './statsMount';
import { mountControlsTab } from './controlsTabMount';
import { mountSceneGuides } from './sceneGuidesMount';
import { updateSceneGuides } from './visualization/coordinateGrid';
import { AdaptivePointRenderer } from './visualization/AdaptivePointRenderer';
import { filesState } from './state/files.svelte';
import { NULL_GPU_TIMER, createGpuTimer } from './rendering/gpuTimer';
import { WEBGPU_CAVEATS, createViewerRenderer } from './rendering/rendererBackend';
import { WebGPUVisibilityRenderer, } from './rendering/WebGPUVisibilityRenderer';
import { FileEntryRegistry } from './state/fileEntries';
import { insertEntryState, removeEntryState } from './state/fileEntryState';
import { uiState } from './state/ui.svelte';
import { acceptVolumeResponse, setVolumeError, updateVolumeProgress } from './state/volume.svelte';
import { flushSync } from 'svelte';
import { ColorProcessor } from './colorProcessor';
import { DepthConverter } from './depth/DepthConverter';
import { DepthWorkerClient } from './depth/DepthWorkerClient';
import { alignSourceOrigin } from './utils/sourceOrigin';
import { SectionPlaneManager } from './visualization/sectionPlanes';
import { normalizeDepth, projectToPointCloud } from './depth/DepthProjector';
import { initTiffWasm, projectDepthBandWasmSync, projectDepthWasmSync, } from './depth/readers/tiffWasm';
import { projectDepthInBands } from './depth/depthProjectionPool';
import { registrationState } from './state/registration.svelte';
import { inspectNpyWasm, isNpyPointCloudShape, parsePlyFromResponse, parsePlyWasm, readNpyWasm, } from './parsers/pointcloudWasm';
import { buildVolumePointsAsync } from './visualization/volumePoints';
import { buildVolumeMeshAsync } from './visualization/isosurface';
import { buildVolumeSlicesAsync } from './visualization/volumeSlices';
import { buildVolumeVoxelsAsync } from './visualization/volumeVoxels';
/**
 * Modern point cloud visualizer with unified file management and Depth image processing
 * Works in both VSCode extension and standalone browser environments
 */
class PointCloudVisualizer {
    optimizeForPointCount(material, _pointCount) {
        pointCloudRenderer.optimizeForPointCount(material, this.allowTransparency);
    }
    /**
     * Make a PointsMaterial decode its (raw 8-bit sRGB) vertex colors to linear in
     * the fragment shader, so we can store point colors as Uint8 (4x less memory)
     * instead of a baked Float32 [0,1] array — see buildOriginalColorArray.
     *
     * The decode is gated by material.userData.srgbDecode and uses the exact same
     * sRGB→linear formula as colorProcessor's LUT, so the result is visually
     * identical to the old path. It only applies in 'original' color mode with the
     * gamma toggle on; intensity/assigned colors (already linear) keep srgbDecode
     * false. Wired through onBeforeCompile (a no-op when disabled) with a matching
     * customProgramCacheKey so toggling recompiles correctly. Idempotent.
     */
    /**
     * Whether a point cloud's vertex colors are raw sRGB that the shader should
     * decode. True only in 'original' mode with the gamma toggle on, and NOT for
     * depth-derived clouds — those colors are already linear, so decoding them
     * would darken them incorrectly. (Intensity/assigned modes aren't 'original'.)
     */
    pointColorsNeedSrgbDecode(data, colorMode) {
        return colorModeUtils.pointColorsNeedSrgbDecode(this, data, colorMode);
    }
    setupPointSrgbDecode(material) {
        colorModeUtils.setupPointSrgbDecode(material);
    }
    toggleTransparency() {
        transparency.toggleTransparency(this);
    }
    updateAllMaterialsForTransparency() {
        transparency.updateAllMaterialsForTransparency(this);
    }
    toggleScreenSpaceScaling() {
        pointSizeScaling.toggleScreenSpaceScaling(this);
    }
    updateAllPointSizesForDistance() {
        pointSizeScaling.updateAllPointSizesForDistance(this);
    }
    calculateScreenSpacePointSize(baseSize, cameraDistance) {
        return pointSizeScaling.calculateScreenSpacePointSize(baseSize, cameraDistance);
    }
    restoreOriginalPointSizes() {
        pointSizeScaling.restoreOriginalPointSizes(this);
    }
    createOptimizedPointCloud(geometry, material) {
        return pointCloudRenderer.createOptimizedPointCloud(geometry, material);
    }
    constructor() {
        /**
         * Whether a VS Code extension host is on the other end of `vscode`.
         *
         * Features that need a process still holding the opened file — the X3A
         * station pipeline re-reads the archive — key off this rather than sniffing
         * for `acquireVsCodeApi`, which cannot be faked in a test without flipping
         * the whole page into webview mode.
         */
        this.runningInVSCode = isVSCode;
        this.vscode = isVSCode
            ? acquireVsCodeApi()
            : {
                // Mock VS Code API for browser version - fully functional
                postMessage: (message) => {
                    console.log('🌐 Browser mode handling:', message.type);
                    this.handleBrowserMessage(message);
                },
            };
        // Browser file handler
        this.browserFileHandler = null;
        /** Which backend `renderer` actually is; see rendering/rendererBackend.ts. */
        this.rendererBackend = 'webgl';
        /**
         * The same renderer as `renderer`, typed concretely — but null on the WebGPU
         * backend. EDL and Spark splat rendering are WebGL-only and use this to tell
         * whether they can run at all.
         */
        this.webglRenderer = null;
        this.sectionPlanes = new SectionPlaneManager();
        /** Original voxel arrays retained for local threshold/mode changes. */
        this.volumeSources = new Map();
        // True between a WebGL context loss and its restoration. While lost, the GPU
        // is gone, so we must not render or touch GL objects — doing so throws and
        // crashes the webview. This is the safety net for the multi-window
        // out-of-VRAM case (each window is a separate context sharing one GPU).
        this.contextLost = false;
        // Camera control state
        this.controlType = 'legacy-trackball';
        this.screenSpaceScaling = false;
        this.allowTransparency = false;
        // Eye Dome Lighting (EDL) state
        // Auto is the default: the pass runs only while at least one visible point
        // cloud uses a uniform material colour. `edlEnabled` remains as a backwards-
        // compatible master switch for benchmarks and integrations.
        this.edlEnabled = true;
        this.edlMode = 'auto';
        this.edlStrength = 1.0;
        this.edlRadius = 1.4;
        this.edlSecondRingWeight = 0.0;
        this.brightnessStops = 0.0;
        this.backgroundBrightness = 13;
        this.effectComposer = null;
        this.edlPass = null;
        this.rotationCenterManager = new RotationCenterManager();
        this.measurementManager = null;
        this.selectionManager = null;
        this.webgpuPickingAvailable = false;
        this.webgpuPickingUnavailableReason = 'WebGPU availability has not been checked';
        this.pointPickingImplementation = 'cpu';
        this.webgpuPointRenderingAvailable = false;
        this.webgpuPointRenderingUnavailableReason = 'WebGPU availability has not been checked';
        this.pointRenderingImplementation = 'current';
        this.webgpuVisibilityRenderer = null;
        // Video mode: camera keyframes, playback and recording (film/FilmManager.ts)
        this.filmManager = null;
        // On-demand rendering state
        this.needsRender = false;
        this.animationId = null;
        this.resizeObserver = null;
        this.screenAnchor = null;
        this.touchNavigation = null;
        this.pointPickGeneration = 0;
        // Welcome message state
        this.isFileLoading = false;
        /** Host-side loads can overlap; the activity dot stays up until all finish. */
        this.pendingFileLoads = 0;
        // When loading additional file(s) into a non-empty scene, we show progress as a
        // row in the Files list instead of a blocking overlay (the current cloud stays
        // interactive). Holds the label/detail of the in-progress add, or null.
        this.pendingLoadLabel = null;
        this.pendingLoadDetail = '';
        // FPS tracking
        this.fpsFrameTimes = [];
        this.lastFpsUpdate = 0;
        this.currentFps = 0;
        this.previousFps = 0;
        // Frame time tracking
        this.lastFrameTime = 0;
        this.frameRenderTimes = [];
        this.currentFrameTime = 0;
        // GPU timing
        // Backend-neutral GPU timing; see rendering/gpuTimer.ts. Starts as the null
        // timer so the render loop needs no guards before the renderer exists.
        this.gpuTimer = NULL_GPU_TIMER;
        /** Default per-point square/round two-pass renderer. */
        this.adaptivePointRenderer = new AdaptivePointRenderer(this);
        this.smallViewAffordance = new SmallViewAffordance(this);
        // Camera tracking for screen-space scaling
        this.lastScalingUpdate = 0;
        // Unified file management
        //
        // The file list addresses spatialFiles, poseGroups and cameraGroups through
        // one index space. fileEntries owns that ordering and hands out the index for
        // each entry, so nothing derives it from collection lengths - see
        // state/fileEntries.ts for why that derivation was unsafe.
        this.fileEntries = new FileEntryRegistry();
        /** Image names of the loaded COLMAP model, for matching streamed photos. */
        this.colmapImageNames = [];
        this.colmapImagesDone = 0;
        this.spatialFiles = [];
        this.meshes = [];
        this.normalsVisualizers = [];
        this.vertexPointsObjects = []; // Vertex points for triangle meshes
        this.multiMaterialGroups = []; // Multi-material Groups for OBJ files
        this.materialMeshes = []; // Sub-meshes for multi-material OBJ files
        this.fileVisibility = [];
        // Per-file "render as gaussian splats" mode (3DGS PLY files only). The
        // manager lives in visualization/splatMode.ts; this array is parallel to
        // spatialFiles like the render-mode arrays below.
        this.splatModeActive = [];
        this.splatMode = new SplatModeManager(this);
        this.isFirstFileLoad = true; // Track if this is the first file being loaded
        // Universal rendering mode states for each file
        this.solidVisible = []; // Solid mesh rendering
        this.wireframeVisible = []; // Wireframe rendering
        this.pointsVisible = []; // Points rendering
        this.normalsVisible = []; // Normals lines rendering
        this.useOriginalColors = true; // Default to original colors
        this.pointSizes = []; // Individual point sizes for each point cloud
        // Sequence mode state
        this.sequenceMode = false;
        this.sequenceFiles = [];
        this.sequenceIndex = 0;
        this.sequenceTargetIndex = 0;
        this.sequenceDidInitialFit = false;
        this.sequenceTimer = null;
        this.sequenceFps = 2; // ~2 frames per second
        this.isSequencePlaying = false;
        this.sequenceCache = new Map();
        this.sequenceCacheOrder = [];
        this.maxSequenceCache = 6; // keep more frames when navigating back
        this.individualColorModes = []; // Individual color modes: 'original', 'assigned', or color index
        this.appliedMtlColors = []; // Store applied MTL hex colors for each file
        this.appliedMtlNames = []; // Store applied MTL material names for each file
        this.appliedMtlData = []; // Store applied MTL data for each file
        // Per-file Depth data storage for reprocessing
        this.fileDepthData = new Map();
        // Depth converter instance
        this.depthConverter = new DepthConverter();
        this.depthWorkerClient = new DepthWorkerClient(this.depthConverter);
        // Pose entries managed like files but stored as Object3D groups
        this.poseGroups = [];
        this.poseMeta = [];
        // Per-pose feature toggles
        this.poseUseDatasetColors = [];
        this.poseShowLabels = [];
        this.poseScaleByScore = [];
        this.poseScaleByUncertainty = [];
        this.poseConvention = [];
        this.poseMinScoreThreshold = [];
        this.poseMaxUncertaintyThreshold = [];
        this.poseLabelsGroups = [];
        this.poseJoints = [];
        this.poseEdges = [];
        // Camera visualization
        this.cameraGroups = [];
        this.cameraNames = [];
        this.cameraVisibility = true;
        this.cameraShowLabels = [];
        this.cameraShowCoords = [];
        // Rotation matrices
        this.cameraMatrix = new THREE.Matrix4(); // Current camera position and rotation
        this.transformationMatrices = []; // Individual transformation matrices for each point cloud
        this.frameCount = 0; // Frame counter for UI updates
        this.lastCameraPosition = new THREE.Vector3(); // Track camera position changes
        this.lastCameraQuaternion = new THREE.Quaternion(); // Track camera rotation changes
        this.lastRotationCenter = new THREE.Vector3(); // Track rotation center changes
        this.arcballInvertRotation = false; // preference for arcball handedness
        // Lighting/material toggles
        this.useUnlitPly = false;
        this.useFlatLighting = false;
        // UI state for collapsible file sections
        this.fileItemsCollapsed = [];
        this.lightingMode = 'normal';
        // Large file chunked loading state
        this.chunkedFileState = new Map();
        // Adaptive decimation tracking
        // Depth processing state - support multiple pending Depth files
        this.pendingDepthFiles = new Map();
        // Dataset texture storage for later application to point clouds
        this.datasetTextures = new Map();
        // Depth conversion tracking
        this.originalDepthFileName = null;
        this.currentCameraParams = null;
        this.depthDimensions = null;
        this.liveDepthUpdateFiles = new Set();
        this.liveDepthUpdateInFlight = new Set();
        this.liveDepthUpdateQueued = new Set();
        this.liveDepthUpdateTimers = new Map();
        this.liveDepthUpdateVersions = new Map();
        this.useLinearColorSpace = true; // Default: toggle is inactive; renderer still outputs sRGB
        this.axesPermanentlyVisible = false; // Persistent axes visibility toggle
        // Color space handling: always output sRGB, optionally convert source sRGB colors to linear before shading
        // Default depth settings for new files
        this.defaultDepthSettings = {
            fx: 1000,
            fy: undefined, // Optional, defaults to fx if not provided
            cx: undefined, // Will be auto-calculated per image based on dimensions
            cy: undefined, // Will be auto-calculated per image based on dimensions
            cameraModel: 'pinhole-opencv',
            depthType: 'euclidean',
            convention: 'opengl',
            pngScaleFactor: 1000, // Default for PNG files
            depthScale: 1.0, // Default scale factor for mono depth networks
            depthBias: 0.0, // Default bias for mono depth networks
        };
        // Color image loader and processor
        this.colorImageLoader = new ColorImageLoader();
        this.colorProcessor = new ColorProcessor();
        this.convertSrgbToLinear = true; // Default: remove gamma from source colors
        this.lastGeometryMs = 0;
        this.lastAbsoluteMs = 0;
        // Predefined colors for different files - use shared constants
        this.fileColors = DEFAULT_COLORS.FILE_COLORS;
        this.modelLastFrameTime = performance.now();
        this.currentTiming = null;
        // Forward PERF lines to the extension's "3D Visualizer" Output channel.
        if (isVSCode) {
            setPerfSink((line) => this.vscode.postMessage({ type: 'perfLog', line }));
        }
        // Splat mode reaches private visibility/button updates through these.
        this.splatMode.refreshVisibility = fileIndex => this.updateMeshVisibilityAndMaterial(fileIndex);
        this.splatMode.refreshButtons = () => {
            renderModeToggles.updateUniversalRenderButtonStates(this);
            // FileItem's mode-dependent controls are declarative; wake Svelte after
            // the asynchronous Spark state changes.
            filesState.renderModeTick += 1;
        };
        this.init();
    }
    async init() {
        try {
            await this.initThreeJS();
            this.applyEnvironmentSpecificUI();
            this.setupEventListeners();
            mountSvelteSmokeTest();
            mountErrorOverlay();
            mountLoadingOverlay();
            mountFileActivityIndicator();
            mountPerformanceStats();
            mountTabNav(this);
            const panel = document.getElementById('main-ui-panel');
            if (panel) {
                this.screenAnchor = new ScreenAnchorController({
                    camera: this.camera,
                    canvas: this.renderer.domElement,
                    panel,
                    onChange: safeRect => this.applyControlScreenRegion(safeRect),
                });
            }
            // Setup color image loader callback
            this.colorImageLoader.setStatusCallback((message, type) => {
                this.showColorMappingStatus(message, type);
            });
            // Setup welcome message interactivity
            mountWelcomeMessage(() => this.triggerOpenFile(), () => browserFileDragDrop.loadExamplePointCloud(this, 'guided'), () => browserFileDragDrop.loadExamplePointCloud(this, 'basic'));
            // Initial check for formatted welcome message
            this.updateWelcomeMessageVisibility();
            // Setup drag handle in both environments
            this.setupPanelResizeAndDrag();
            this.setupBrowserFileHandlers();
            if (isVSCode) {
                // VSCode extension environment
                configureRegistrationExtensionHost(message => this.vscode.postMessage(message));
                this.setupMessageHandler();
                // The extension may already be parsing a restored document, but it
                // queues all outbound messages until this listener is installed.
                this.vscode.postMessage({ type: 'webviewReady' });
                console.log('📤 Requesting default depth settings from extension...');
                this.vscode.postMessage({
                    type: 'requestDefaultDepthSettings',
                });
            }
            else {
                // Browser environment
                this.initializeBrowserFileHandler();
                installBrowserAgent(this);
                console.log('🌐 Initializing standalone browser version...');
            }
            // Hosts may deliver their first file only after the asynchronous renderer
            // and file/message listeners are ready (construction alone is too early).
            document.documentElement.dataset.visualizerReady = 'true';
            window.dispatchEvent(new Event('visualizer-ready'));
        }
        catch (error) {
            this.showError(`Failed to initialize 3D Visualizer: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
    /**
     * Asynchronous only because of the WebGPU backend, which needs a dynamic
     * import and an `await renderer.init()` before its first draw. Nothing here
     * may render before that await completes — the render loop starts later.
     */
    async initThreeJS() {
        // Scene
        this.scene = new THREE.Scene();
        this.applyBackgroundBrightness();
        // Camera
        const container = document.getElementById('viewer-container');
        if (!container) {
            throw new Error('Viewer container not found');
        }
        this.camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, FIXED_CAMERA_NEAR, FIXED_CAMERA_FAR);
        this.camera.position.set(1, 1, 1);
        // Initialize last camera state for change detection
        this.lastCameraPosition.copy(this.camera.position);
        this.lastCameraQuaternion.copy(this.camera.quaternion);
        // Renderer
        const canvas = document.getElementById('three-canvas');
        if (!canvas) {
            throw new Error('Canvas not found');
        }
        // Backend selection and construction both live in rendering/rendererBackend.ts;
        // WebGL stays the default and every WebGPU failure falls back to it.
        const selection = await createViewerRenderer(canvas);
        this.renderer = selection.renderer;
        this.rendererBackend = selection.backend;
        this.webglRenderer = selection.webglRenderer;
        this.sectionPlanes.attachRenderer(this.renderer);
        if (selection.fallbackReason) {
            console.warn(`⚠️ Falling back to WebGL. ${selection.fallbackReason}`);
        }
        if (this.rendererBackend === 'webgpu') {
            console.log('🧪 WebGPU backend active. Read measurements with these in mind:');
            for (const caveat of WEBGPU_CAVEATS) {
                console.log(`   • ${caveat}`);
            }
        }
        this.renderer.setSize(container.clientWidth, container.clientHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.applySceneBrightness();
        this.applyBackgroundBrightness();
        // Shadows are disabled: no object in the scene sets castShadow/receiveShadow,
        // so the per-frame shadow pass + 2048² depth target produced nothing visible
        // — pure overhead. (If shadow-casting meshes are ever added, re-enable here
        // and set castShadow/receiveShadow on them.)
        this.renderer.shadowMap.enabled = false;
        // Not `canvas`: a failed WebGPU start swaps in a fresh element, because a
        // canvas keeps the first graphics context it was given.
        this.setupContextLossHandling(this.renderer.domElement);
        // Initial check for formatted welcome message
        this.updateWelcomeMessageVisibility();
        // Initialize GPU timing if supported
        this.gpuTimer = createGpuTimer(this.renderer);
        // Re-enable object sorting for better visual quality
        this.renderer.sortObjects = true;
        // Initialize EDL post-processing pipeline
        this.initEDLComposer();
        // Set initial color space based on preference
        this.updateRendererColorSpace();
        // Initialize controls
        this.initializeControls();
        this.touchNavigation = new TouchNavigationController({
            camera: this.camera,
            element: this.renderer.domElement,
            getControls: () => this.controls,
            onChange: () => this.requestRender(),
            onDoubleTap: (clientX, clientY) => {
                void this.onDoubleClick(new MouseEvent('dblclick', { bubbles: true, clientX, clientY }));
            },
        });
        // Initialize measurement manager
        this.measurementManager = new MeasurementManager(this.scene, this.camera, this.renderer, this);
        // Initialize selection manager
        this.selectionManager = new SelectionManager(this.getSelectionContext());
        await this.selectionManager.initializeWebGPUPicker();
        this.webgpuPickingAvailable = this.selectionManager.isWebGPUPickingAvailable();
        this.webgpuPickingUnavailableReason = this.selectionManager.getWebGPUUnavailableReason();
        this.pointPickingImplementation = this.selectionManager.getPointPickingImplementation();
        console.log(this.webgpuPickingAvailable
            ? '⚡ WebGPU point picking available and enabled'
            : `ℹ️ WebGPU point picking unavailable; using CPU (${this.webgpuPickingUnavailableReason})`);
        const visibilitySelection = await WebGPUVisibilityRenderer.create(container);
        this.webgpuVisibilityRenderer = visibilitySelection.renderer;
        this.webgpuPointRenderingAvailable = visibilitySelection.renderer !== null;
        this.webgpuPointRenderingUnavailableReason = visibilitySelection.unavailableReason;
        // Initialize video mode (camera keyframes / recording)
        this.filmManager = new FilmManager(this);
        // Lighting
        this.initSceneLighting();
        // Add coordinate axes helper with labels
        this.addAxesHelper();
        // Window resize with ResizeObserver for comprehensive dimension change detection
        window.addEventListener('resize', this.onWindowResize.bind(this));
        this.setupResizeObserver();
        // Global UI interaction listener - triggers render on any button/input change
        document.addEventListener('click', e => {
            const target = e.target;
            if (target.tagName === 'BUTTON' ||
                target.classList.contains('btn') ||
                target.closest('button') ||
                target.closest('.btn')) {
                this.requestRender();
                // this.requestRender();
            }
        });
        document.addEventListener('input', e => {
            const target = e.target;
            if (target.tagName === 'INPUT' || target.tagName === 'SELECT') {
                this.requestRender();
                // this.requestRender();
            }
        });
        // Double-click to change the rotation center.
        this.renderer.domElement.addEventListener('dblclick', this.onDoubleClick.bind(this));
        // Start render loop
        this.startRenderLoop();
        // Clean up on page unload
        window.addEventListener('beforeunload', () => {
            this.dispose();
        });
    }
    initializeControls() {
        // Store current camera state before disposing old controls
        const currentCameraPosition = this.camera.position.clone();
        const currentTarget = this.controls ? this.controls.target.clone() : new THREE.Vector3(0, 0, 0);
        const currentUp = this.camera.up.clone();
        // Dispose of existing controls if any
        if (this.controls) {
            this.controls.dispose();
        }
        if (this.controlType === 'trackball') {
            // Optional sphere-projected "virtual ball" trackball: center drags
            // orbit, while rim/tangential drags roll the scene under the cursor.
            this.controls = new VirtualBallControls(this.camera, this.renderer.domElement);
            const ball = this.controls;
            // Speed calibration against the legacy trackball, in the round-ball
            // units (both axes normalized by the smaller canvas half-dimension):
            // legacy rotates 5/halfWidth rad per pixel; the ball rotates
            // rotateSpeed/halfMin — parity on a 16:9 canvas is ≈ 2.8. Defaults sit
            // ~25% above parity because the ball uses net displacement (endpoint
            // chord) while legacy integrates every wobble of the hand path, which
            // makes equal nominal speeds feel slower on the ball. zoomSpeed has
            // legacy TrackballControls semantics (see VirtualBallControls).
            ball.rotateSpeed = 3.5;
            ball.rollSpeed = 3.0;
            ball.zoomSpeed = 2.5;
            ball.panSpeed = 1.5;
        }
        else if (this.controlType === 'legacy-trackball') {
            // Default: three.js delta-based TrackballControls.
            this.controls = new TrackballControls(this.camera, this.renderer.domElement);
            const trackballControls = this.controls;
            trackballControls.rotateSpeed = 5.0;
            trackballControls.zoomSpeed = 2.5;
            trackballControls.panSpeed = 1.5;
            trackballControls.noZoom = false;
            trackballControls.noPan = false;
            trackballControls.staticMoving = false;
            trackballControls.dynamicDampingFactor = 0.2;
            // Set up screen coordinates for proper rotation
            trackballControls.screen.left = 0;
            trackballControls.screen.top = 0;
            trackballControls.screen.width = this.renderer.domElement.clientWidth;
            trackballControls.screen.height = this.renderer.domElement.clientHeight;
        }
        else if (this.controlType === 'arcball') {
            this.controls = new CustomArcballControls(this.camera, this.renderer.domElement);
            const arc = this.controls;
            arc.rotateSpeed = 1.0;
            arc.zoomSpeed = 1.0;
            arc.panSpeed = 1.0;
            // Apply preference
            arc.invertRotation = this.arcballInvertRotation;
        }
        else {
            this.controls = new OrbitControls(this.camera, this.renderer.domElement);
            const orbitControls = this.controls;
            orbitControls.enableDamping = true;
            orbitControls.dampingFactor = 0.2;
            orbitControls.screenSpacePanning = false;
            orbitControls.minDistance = 0.001;
            orbitControls.maxDistance = 50000; // Increased to match camera far plane
        }
        // Set up axes visibility for all control types
        this.setupAxesVisibility();
        // Restore camera state to prevent jumps
        this.camera.position.copy(currentCameraPosition);
        this.camera.up.copy(currentUp);
        this.controls.target.copy(currentTarget);
        this.controls.update();
        const safeRect = this.screenAnchor?.getSafeRect();
        if (safeRect) {
            this.applyControlScreenRegion(safeRect);
        }
        // Initialize rotation center tracking
        this.lastRotationCenter.copy(this.controls.target);
        // Update control status to highlight active button
        this.updateControlStatus();
    }
    applyControlScreenRegion(safeRect) {
        const controls = this.controls;
        controls.setScreenRegion?.(safeRect);
        this.requestRender();
    }
    setupAxesVisibility() {
        axesFeature.setupAxesVisibility(this);
    }
    addAxesHelper() {
        axesFeature.addAxesHelper(this);
    }
    createAxisLabels(axesGroup) {
        axesFeature.createAxisLabels(this, axesGroup);
    }
    initSceneLighting() {
        // Remove existing lights
        const lightsToRemove = this.scene.children.filter(child => child instanceof THREE.AmbientLight ||
            child instanceof THREE.DirectionalLight ||
            child instanceof THREE.HemisphereLight);
        lightsToRemove.forEach(light => this.scene.remove(light));
        // Add fresh lighting based on mode
        if (this.useFlatLighting) {
            const ambient = new THREE.AmbientLight(0xffffff, 0.9);
            this.scene.add(ambient);
            const hemi = new THREE.HemisphereLight(0xffffff, 0x888888, 0.6);
            this.scene.add(hemi);
        }
        else {
            const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
            this.scene.add(ambientLight);
            const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
            directionalLight.position.set(10, 10, 5);
            directionalLight.castShadow = true;
            directionalLight.shadow.mapSize.width = 2048;
            directionalLight.shadow.mapSize.height = 2048;
            this.scene.add(directionalLight);
        }
        // Ensure initial UI states reflect current settings
        setTimeout(() => {
            this.updateGammaButtonState();
            this.updateAxesButtonState();
            this.updateLightingButtonsState();
            this.updateRotationCenterModeButtons();
        }, 0);
    }
    updateLightingButtonsState() {
        const normalBtn = document.getElementById('use-normal-lighting');
        const flatBtn = document.getElementById('use-flat-lighting');
        if (normalBtn && flatBtn) {
            if (this.lightingMode === 'flat') {
                normalBtn.classList.remove('active');
                flatBtn.classList.add('active');
            }
            else if (this.lightingMode === 'normal') {
                flatBtn.classList.remove('active');
                normalBtn.classList.add('active');
            }
            else {
                // Unlit mode: neither normal nor flat highlighted
                flatBtn.classList.remove('active');
                normalBtn.classList.remove('active');
            }
        }
        const unlitBtn = document.getElementById('toggle-unlit-ply');
        if (unlitBtn) {
            if (this.lightingMode === 'unlit') {
                unlitBtn.classList.add('active');
            }
            else {
                unlitBtn.classList.remove('active');
            }
        }
    }
    updateRendererColorSpace() {
        sceneBrightness.updateRendererColorSpace(this);
    }
    applySceneBrightness() {
        sceneBrightness.applySceneBrightness(this);
    }
    getBackgroundCssColor() {
        return sceneBrightness.getBackgroundCssColor(this);
    }
    getBackgroundBrightnessLabel() {
        return sceneBrightness.getBackgroundBrightnessLabel(this);
    }
    applyBackgroundBrightness() {
        sceneBrightness.applyBackgroundBrightness(this);
    }
    applyEnvironmentSpecificUI() {
        // Themes are browser-only; VS Code uses native theme variables.
        const themeSection = document.getElementById('theme-section');
        if (themeSection && isVSCode) {
            themeSection.style.display = 'none';
        }
    }
    toggleGammaCorrection() {
        sceneBrightness.toggleGammaCorrection(this);
    }
    updateGammaButtonState() {
        sceneBrightness.updateGammaButtonState(this);
    }
    updateRotationCenterModeButtons() {
        this.rotationCenterManager.updateModeButtons();
    }
    rebuildAllColorAttributesForCurrentGammaSetting() {
        sceneBrightness.rebuildAllColorAttributesForCurrentGammaSetting(this);
    }
    setupResizeObserver() {
        const container = document.getElementById('viewer-container');
        if (!container) {
            return;
        }
        this.resizeObserver = new ResizeObserver(() => {
            // Trigger rerender when container dimensions change
            this.onWindowResize();
        });
        this.resizeObserver.observe(container);
    }
    /**
     * Get the selection context for the SelectionManager
     */
    getSelectionContext() {
        return {
            fileEntries: this.fileEntries,
            camera: this.camera,
            meshes: this.meshes,
            spatialFiles: this.spatialFiles,
            poseGroups: this.poseGroups,
            cameraGroups: this.cameraGroups,
            fileVisibility: this.fileVisibility,
            pointSizes: this.pointSizes,
            screenSpaceScaling: this.screenSpaceScaling,
            splatMeshes: this.spatialFiles.map((_, index) => this.splatMode.getMesh(index)),
            clippingPlanes: this.renderer.clippingPlanes,
        };
    }
    dispose() {
        for (const data of this.spatialFiles) {
            data.sceneModel?.dispose();
        }
        // Clean up ResizeObserver
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        this.touchNavigation?.dispose();
        this.touchNavigation = null;
        this.screenAnchor?.dispose();
        this.screenAnchor = null;
        // Clean up measurements
        if (this.measurementManager) {
            this.measurementManager.dispose();
            this.measurementManager = null;
        }
        if (this.selectionManager) {
            this.selectionManager.dispose();
            this.selectionManager = null;
        }
        this.webgpuVisibilityRenderer?.dispose();
        this.webgpuVisibilityRenderer = null;
        // Clean up video mode
        if (this.filmManager) {
            this.filmManager.dispose();
            this.filmManager = null;
        }
        // Clean up EDL resources
        if (this.edlPass) {
            this.edlPass.dispose();
            this.edlPass = null;
        }
        if (this.effectComposer) {
            this.effectComposer = null;
        }
        // Clean up controls
        if (this.controls) {
            this.controls.dispose();
        }
        // Clean up renderer
        if (this.renderer) {
            this.renderer.dispose();
        }
    }
    showLoading(show, message) {
        this.isFileLoading = show;
        // In VS Code the extension host sends backgroundOperationComplete only
        // after parsing, progressive colour delivery, and every other load phase
        // has finished. Geometry becoming visible is not that terminal event.
        if (show || !this.runningInVSCode) {
            uiState.fileLoading = show;
        }
        if (show) {
            uiState.loadingVisible = true;
            if (message) {
                uiState.loadingTitle = message;
            }
        }
        else {
            uiState.loadingVisible = false;
            // Clear any in-progress Files-list loading row.
            if (this.pendingLoadLabel !== null) {
                this.pendingLoadLabel = null;
                this.pendingLoadDetail = '';
                this.updateFileList();
            }
        }
        // Update welcome message state based on loading status
        this.updateWelcomeMessageVisibility();
    }
    updateWelcomeMessageVisibility() {
        uiStatus.updateWelcomeMessageVisibility(this);
    }
    triggerOpenFile() {
        if (isVSCode) {
            this.vscode.postMessage({
                type: 'addFile',
            });
        }
        else {
            const fileInput = document.getElementById('hiddenFileInput');
            if (fileInput) {
                fileInput.click();
            }
        }
    }
    onWindowResize() {
        const container = document.getElementById('viewer-container');
        if (!container) {
            return;
        }
        this.camera.aspect = container.clientWidth / container.clientHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(container.clientWidth, container.clientHeight);
        this.screenAnchor?.refreshAutomatic();
        // Update EDL composer and render targets on resize
        if (this.effectComposer) {
            this.effectComposer.setSize(container.clientWidth, container.clientHeight);
        }
        // Update controls based on type (TrackballControls-based schemes need
        // their screen rectangle refreshed on resize; VirtualBallControls reads
        // the live canvas rect on every event and needs nothing here)
        if (this.controlType === 'legacy-trackball') {
            const trackballControls = this.controls;
            trackballControls.screen.width = container.clientWidth;
            trackballControls.screen.height = container.clientHeight;
            trackballControls.handleResize();
        }
        else {
            const orbitControls = this.controls;
            // OrbitControls automatically handles resize
        }
        // Force immediate render to prevent flashing
        const now = performance.now();
        if (this.lastFrameTime > 0) {
            this.trackFrameTime(now - this.lastFrameTime);
        }
        this.lastFrameTime = now;
        // Start GPU timing for resize render
        this.gpuTimer.begin();
        this.performRender();
        this.gpuTimer.end();
        this.gpuTimer.poll();
        // Track render event for resize renders too
        this.trackRender();
    }
    animate() {
        const modelNow = performance.now();
        updateModelPlayback(this, Math.min((modelNow - this.modelLastFrameTime) / 1000, 0.1));
        this.modelLastFrameTime = modelNow;
        this.animationId = requestAnimationFrame(this.animate.bind(this));
        // Update FPS calculation (always, to decay to 0 when no renders)
        this.updateFPSCalculation();
        // Update controls
        this.controls.update();
        // Update measurement label positions
        if (this.measurementManager) {
            this.measurementManager.updateLabelPositions();
        }
        // Check if camera position, rotation, or rotation center has changed
        const positionChanged = !this.camera.position.equals(this.lastCameraPosition);
        const rotationChanged = !this.camera.quaternion.equals(this.lastCameraQuaternion);
        const rotationCenterChanged = !this.controls.target.equals(this.lastRotationCenter);
        // Only update camera matrix and UI when camera actually changes
        if (positionChanged || rotationChanged) {
            this.updateCameraMatrix();
            this.updateCameraControlsPanel();
            // Update screen-space scaling if enabled
            if (this.screenSpaceScaling) {
                const now = performance.now();
                // Throttle updates to every 100ms for performance
                if (now - this.lastScalingUpdate > 100) {
                    this.updateAllPointSizesForDistance();
                    this.lastScalingUpdate = now;
                }
            }
            // Debug: Check if any Depth-derived point clouds are being culled
            // Only log every 60 frames to avoid spam
            this.frameCount++;
            if (this.frameCount % 60 === 0) {
                this.checkMeshVisibility();
            }
            // Update last known position and rotation
            this.lastCameraPosition.copy(this.camera.position);
            this.lastCameraQuaternion.copy(this.camera.quaternion);
            this.needsRender = true;
        }
        // Handle rotation center changes separately
        if (rotationCenterChanged) {
            // Update coordinate system position to follow the rotation center
            const axesGroup = this.axesGroup;
            if (axesGroup) {
                axesGroup.position.copy(this.controls.target);
            }
            // Update reset to center button state
            this.updateRotationOriginButtonState();
            // Update last known rotation center
            this.lastRotationCenter.copy(this.controls.target);
            this.needsRender = true;
        }
        // Always render when needed (this covers camera damping/momentum)
        if (this.needsRender) {
            // Consume the current invalidation before rendering. Renderer callbacks
            // (notably Spark's asynchronous sorter) may request another frame while
            // performRender() is running; that request must survive for the next RAF.
            this.needsRender = false;
            const now = performance.now();
            // Measure full frame time (time between actual renders)
            if (this.lastFrameTime > 0) {
                this.trackFrameTime(now - this.lastFrameTime);
            }
            this.lastFrameTime = now;
            // Start GPU timing
            this.gpuTimer.begin();
            this.performRender();
            this.gpuTimer.end();
            // Update GPU timing results
            this.gpuTimer.poll();
            // Track render event
            this.trackRender();
        }
    }
    requestRender() {
        this.needsRender = true;
    }
    /**
     * Recover gracefully from WebGL context loss instead of crashing.
     *
     * Context loss happens when the GPU runs out of memory — common when several
     * extension windows each hold a large cloud, since every webview is a separate
     * WebGL context but they all share one GPU's VRAM. Without this handler the
     * next GL call throws and takes down the whole webview. We preventDefault() to
     * let the browser attempt restoration, pause rendering while lost, and resume
     * on restore (Three.js re-uploads geometries/textures automatically on the
     * next render because their CPU-side arrays still exist).
     */
    setupContextLossHandling(canvas) {
        // `webglcontextlost` never fires for a WebGPU canvas; that backend reports
        // device loss through the renderer instead. Nothing listens for it yet, so
        // a lost WebGPU device is simply not recovered from — acceptable while the
        // backend is an opt-in experiment, and noted in docs/WEBGPU_READINESS.md.
        if (this.rendererBackend !== 'webgl') {
            return;
        }
        canvas.addEventListener('webglcontextlost', event => {
            event.preventDefault(); // required so the context can be restored
            this.contextLost = true;
            console.warn('WebGL context lost — pausing rendering until restored.');
            this.showStatus('GPU context lost (likely out of memory). Recovering…');
        }, false);
        canvas.addEventListener('webglcontextrestored', () => {
            this.contextLost = false;
            console.warn('WebGL context restored — resuming.');
            this.showStatus('GPU context restored.');
            this.requestRender();
        }, false);
    }
    /**
     * Centralized render method — routes through EDL EffectComposer when enabled,
     * falls back to direct renderer.render() when disabled for zero overhead.
     */
    performRender() {
        // The GPU is gone while the context is lost; any GL call would throw.
        if (this.contextLost) {
            return;
        }
        updateAgentPointSizes(this);
        this.adaptivePointRenderer.beforeRender();
        if (renderAgentComparison(this)) {
            return;
        }
        this.smallViewAffordance.update();
        updateSceneGuides(this);
        const visibilityContext = this.getVisibilityRenderContext();
        const useEDL = edl.prepareEDLFrame(this);
        const useVisibilityRenderer = this.pointRenderingImplementation === 'webgpu-visibility' &&
            !useEDL &&
            !this.allowTransparency &&
            !!this.webgpuVisibilityRenderer &&
            this.webgpuVisibilityRenderer.canRender(visibilityContext);
        if (useVisibilityRenderer && this.webgpuVisibilityRenderer) {
            const visibility = visibilityContext.pointClouds.map(cloud => cloud.visible);
            for (const cloud of visibilityContext.pointClouds) {
                cloud.visible = false;
            }
            try {
                this.renderer.render(this.scene, this.camera);
            }
            finally {
                visibilityContext.pointClouds.forEach((cloud, index) => (cloud.visible = visibility[index]));
            }
            try {
                this.webgpuVisibilityRenderer.setEnabled(true);
                if (!this.webgpuVisibilityRenderer.render(visibilityContext)) {
                    this.webgpuVisibilityRenderer.setEnabled(false);
                    this.renderer.render(this.scene, this.camera);
                }
            }
            catch (error) {
                console.warn(`WebGPU visibility rendering failed; using current renderer: ${error instanceof Error ? error.message : String(error)}`);
                this.webgpuVisibilityRenderer.setEnabled(false);
                this.renderer.render(this.scene, this.camera);
            }
            return;
        }
        this.webgpuVisibilityRenderer?.setEnabled(false);
        if (useEDL && this.effectComposer) {
            this.effectComposer.render();
        }
        else {
            this.renderer.render(this.scene, this.camera);
        }
    }
    getVisibilityRenderContext() {
        const pointClouds = this.meshes.filter((mesh, index) => !!mesh &&
            this.fileVisibility[index] &&
            mesh.visible &&
            mesh instanceof THREE.Points &&
            mesh.material instanceof THREE.PointsMaterial);
        return {
            camera: this.camera,
            pointClouds,
            clippingPlanes: this.renderer.clippingPlanes,
            brightnessStops: this.brightnessStops,
            hasDepthConflicts: this.meshes.some((mesh, index) => !!mesh && this.fileVisibility[index] && mesh.visible && !(mesh instanceof THREE.Points)),
        };
    }
    /**
     * Initialize the EDL post-processing pipeline.
     * Creates the EffectComposer with a RenderPass and EDLPass.
     * The composer is only used when EDL is enabled.
     */
    initEDLComposer() {
        edl.initEDLComposer(this);
    }
    /**
     * Toggle Eye Dome Lighting on/off.
     */
    toggleEDL() {
        edl.toggleEDL(this);
    }
    /**
     * Update EDL button active state.
     */
    updateEDLButtonState() {
        edl.updateEDLButtonState(this);
    }
    /**
     * Show/hide the EDL strength and radius sliders.
     */
    updateEDLSettingsVisibility() {
        edl.updateEDLSettingsVisibility(this);
    }
    trackRender() {
        renderStats.trackRender(this);
    }
    trackFrameTime(frameTimeMs) {
        renderStats.trackFrameTime(this, frameTimeMs);
    }
    updateFPSCalculation() {
        renderStats.updateFPSCalculation(this);
    }
    updateFPSDisplay() {
        renderStats.updateFPSDisplay(this);
    }
    startRenderLoop() {
        if (this.animationId === null) {
            this.animate();
        }
    }
    stopRenderLoop() {
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }
    checkMeshVisibility() {
        // Check if any meshes are being culled by frustum culling
        for (let i = 0; i < this.meshes.length; i++) {
            const mesh = this.meshes[i];
            const isVisible = this.fileVisibility[i];
            if (!isVisible) {
                continue;
            } // Skip if manually hidden
            // Check if mesh should be visible but might be culled
            if (mesh && mesh.geometry && mesh.geometry.boundingBox) {
                const box = mesh.geometry.boundingBox.clone();
                box.applyMatrix4(mesh.matrixWorld);
                // Simple frustum check - if bounding box is completely outside view
                const center = box.getCenter(new THREE.Vector3());
                const distanceToCamera = this.camera.position.distanceTo(center);
                // Check if it's within camera range
                const withinNearFar = distanceToCamera >= this.camera.near && distanceToCamera <= this.camera.far;
                if (!withinNearFar) {
                    // debug: culling warning
                }
                // Check if bounding box is extremely large
                const size = box.getSize(new THREE.Vector3());
                const maxDim = Math.max(size.x, size.y, size.z);
                if (maxDim > 50000) {
                    // debug: large bounds
                }
            }
        }
    }
    // Rotation Matrix Methods
    updateCameraMatrix() {
        transformationMatrix.updateCameraMatrix(this);
    }
    setTransformationMatrix(fileIndex, matrix) {
        transformationMatrix.setTransformationMatrix(this, fileIndex, matrix);
    }
    getTransformationMatrix(fileIndex) {
        return transformationMatrix.getTransformationMatrix(this, fileIndex);
    }
    getTransformationMatrixAsArray(fileIndex) {
        return transformationMatrix.getTransformationMatrixAsArray(this, fileIndex);
    }
    applyTransformationMatrix(fileIndex) {
        transformationMatrix.applyTransformationMatrix(this, fileIndex);
    }
    resetTransformationMatrix(fileIndex) {
        transformationMatrix.resetTransformationMatrix(this, fileIndex);
    }
    multiplyTransformationMatrices(fileIndex, matrix) {
        transformationMatrix.multiplyTransformationMatrices(this, fileIndex, matrix);
    }
    addTranslationToMatrix(fileIndex, x, y, z) {
        transformationMatrix.addTranslationToMatrix(this, fileIndex, x, y, z);
    }
    updateMatrixTextarea(fileIndex) {
        transformationMatrix.updateMatrixTextarea(this, fileIndex);
    }
    updateCameraMatrixDisplay() {
        transformationMatrix.updateCameraMatrixDisplay(this);
    }
    updateCameraControlsPanel() {
        transformationMatrix.updateCameraControlsPanel(this);
    }
    resetCameraToDefault() {
        // Reset FOV and camera orientation
        this.camera.fov = 75;
        this.camera.updateProjectionMatrix();
        // Reset quaternion to identity (no rotation)
        this.camera.quaternion.set(0, 0, 0, 1);
        // Fit camera to currently loaded objects
        this.fitCameraToAllObjects();
        // Update last known camera state to prevent unnecessary UI updates
        this.lastCameraPosition.copy(this.camera.position);
        this.lastCameraQuaternion.copy(this.camera.quaternion);
        // Force update camera matrix and UI
        this.updateCameraMatrix();
        this.updateCameraControlsPanel();
    }
    setRotationCenterToOrigin() {
        rotationCenterFeature.setRotationCenterToOrigin(this);
    }
    setPointPickingImplementation(implementation) {
        this.selectionManager?.setPointPickingImplementation(implementation);
        this.pointPickingImplementation =
            this.selectionManager?.getPointPickingImplementation() ?? 'cpu';
        this.showStatus(this.pointPickingImplementation === 'webgpu'
            ? 'Using WebGPU point picking'
            : 'Using CPU point picking');
    }
    setPointRenderingImplementation(implementation) {
        this.pointRenderingImplementation =
            implementation === 'webgpu-visibility' && this.webgpuVisibilityRenderer
                ? 'webgpu-visibility'
                : 'current';
        this.webgpuVisibilityRenderer?.setEnabled(this.pointRenderingImplementation === 'webgpu-visibility');
        this.showStatus(this.pointRenderingImplementation === 'webgpu-visibility'
            ? 'Using WebGPU one-pixel visibility rendering where compatible'
            : 'Using current point rendering');
        this.requestRender();
    }
    async onDoubleClick(event) {
        if (!this.selectionManager) {
            return;
        }
        // Only the most recently started pick may change the viewer. This protects
        // touch double-taps from a later Safari compatibility event and prevents an
        // older asynchronous pick from overwriting a newer result.
        const pickGeneration = ++this.pointPickGeneration;
        // Get canvas and mouse position in screen coordinates
        const canvas = this.renderer.domElement;
        const rect = canvas.getBoundingClientRect();
        const mouseScreenX = event.clientX - rect.left;
        const mouseScreenY = event.clientY - rect.top;
        console.log(`🖱️ Double-click at (${mouseScreenX.toFixed(1)}, ${mouseScreenY.toFixed(1)})`);
        // Update selection context before selecting
        this.selectionManager.updateContext(this.getSelectionContext());
        // Try to select a point with detailed logging
        const result = await this.selectionManager.selectPointWithLoggingAsync(mouseScreenX, mouseScreenY, canvas);
        if (pickGeneration !== this.pointPickGeneration) {
            return;
        }
        if (result) {
            const { point: selectedPoint, info } = result;
            // Log selection info with appropriate emoji
            if (info.includes('camera profile')) {
                console.log(`📷 Selected ${info}`);
            }
            else if (info.includes('pose keypoint')) {
                console.log(`🕺 Selected ${info}`);
            }
            else if (info.includes('triangle mesh')) {
                console.log(`🔷 Selected ${info}`);
            }
            else if (info.includes('gaussian splat')) {
                console.log(`✨ Selected ${info}`);
            }
            else {
                console.log(`⚫ Selected point cloud: ${info}`);
            }
            // Correspondence picking is on Cmd/Ctrl + double-click, not on the plain
            // one. Claiming the plain gesture meant that while picking was armed the
            // only way to move the rotation centre — the thing you need constantly
            // while hunting for the same corner in two clouds — was to stop picking.
            if ((event.metaKey || event.ctrlKey) &&
                registrationFeature.handlePickedPoint(this, selectedPoint)) {
                console.log(`🎯 Registration correspondence point added (${info})`);
                this.requestRender();
                return;
            }
            // Shift + double-click is the single measurement gesture. The first
            // implicit path starts at the rotation center; an explicitly created
            // free path starts at the first picked point instead.
            if ((event.shiftKey || measurementState.pickingEnabled) && this.measurementManager) {
                const rotationCenter = this.controls.target.clone();
                this.measurementManager.prepareForPathPoint(rotationCenter);
                this.measurementManager.addPathPoint(selectedPoint);
                console.log(`📏 Measurement path point added (${info})`);
                this.requestRender();
            }
            else {
                this.setRotationCenter(selectedPoint);
                this.updateRotationOriginButtonState();
            }
            return;
        }
        // A double-click far away from every visible object is the "I'm lost"
        // recovery gesture: refit the view instead of doing nothing. Near-misses
        // next to an object stay inert so a failed pick never jumps the camera.
        // Suppressed for Shift + double-click so a missed measurement pick never
        // jumps the camera.
        if (!this.sequenceMode &&
            !event.shiftKey &&
            !event.metaKey &&
            !event.ctrlKey &&
            this.selectionManager.isFarFromAllVisibleObjects(mouseScreenX, mouseScreenY, canvas)) {
            console.log('🧭 Double-click in empty space - fitting view to all objects');
            this.fitCameraToAllObjects();
            return;
        }
        // If no point found, log the failure
        console.log(`❌ No selectable object found at (${mouseScreenX.toFixed(1)}, ${mouseScreenY.toFixed(1)})`);
    }
    setRotationCenter(point) {
        rotationCenterFeature.setRotationCenter(this, point);
    }
    showRotationCenterFeedback(point) {
        rotationCenterFeature.showRotationCenterFeedback(this, point);
    }
    getIntensityArray(data) {
        return intensity.getIntensityArray(data);
    }
    hasIntensityData(data) {
        return intensity.hasIntensityData(data);
    }
    buildIntensityColorArrayForMode(values, pointCount, colorMode) {
        return intensity.buildIntensityColorArrayForMode(values, pointCount, colorMode);
    }
    mapIntensityValue(value, mapName) {
        return intensity.mapIntensityValue(value, mapName);
    }
    // For POINT CLOUDS this returns raw 8-bit sRGB colors (Uint8, 3 bytes/point)
    // and the sRGB→linear conversion happens in the point shader (see
    // setupPointSrgbDecode). That's 4x less color memory than the old baked Float32
    // [0,1] array — the key lever when many large clouds are open at once — and the
    // typed path is zero-copy (shares the parser's array). MESHES are unchanged:
    // they keep the Float32 + LUT path (few vertices, memory is a non-issue, and
    // their lit materials read linear vertex colors directly).
    buildOriginalColorArray(data) {
        return colorModeUtils.buildOriginalColorArray(this, data);
    }
    applyColorModeToGeometry(data, geometry, colorMode) {
        colorModeUtils.applyColorModeToGeometry(this, data, geometry, colorMode);
    }
    shouldUseVertexColors(data, colorMode) {
        return colorModeUtils.shouldUseVertexColors(data, colorMode);
    }
    createGeometryFromSpatialData(data) {
        const { geometry, geometryMs } = meshBuilder.createGeometryFromSpatialData(this, data);
        this.lastGeometryMs = geometryMs;
        return geometry;
    }
    setupEventListeners() {
        // Add file button - different behavior for VSCode vs browser
        const addFileBtn = document.getElementById('add-file');
        if (addFileBtn) {
            addFileBtn.addEventListener('click', () => {
                if (isVSCode) {
                    // VSCode environment - request file from extension
                    this.requestAddFile();
                }
                else {
                    // Browser environment - trigger file input
                    const fileInput = document.getElementById('hiddenFileInput');
                    if (fileInput) {
                        fileInput.click();
                    }
                }
            });
        }
        // Sequence controls - Svelte component (components/SequenceControls.svelte),
        // driven by state/ui.svelte.ts's sequence fields.
        mountSequenceControls({
            onPlayPause: () => (this.isSequencePlaying ? this.pauseSequence() : this.playSequence()),
            onPrev: () => this.stepSequence(-1),
            onNext: () => this.stepSequence(1),
            onSeek: index => this.seekSequence(index),
        });
        // File list - Svelte component (components/FileList.svelte), see
        // updateFileList() and docs/SVELTE_MIGRATION_PLAN.md Phase 3.
        mountFileList(this);
        mountGlobalAlignMenu(this);
        mountStats(this);
        mountControlsTab(this);
        mountSceneGuides(this);
        mountFilmPanel(this);
        mountMeasurementQuickActions(this);
        mountSmallViewAffordance(this);
        document.addEventListener('dblclick', e => {
            const slider = e.target;
            if (!(slider instanceof HTMLInputElement) || slider.type !== 'range') {
                return;
            }
            const resetValue = slider.defaultValue || slider.getAttribute('value');
            if (resetValue === null || resetValue === '') {
                return;
            }
            e.preventDefault();
            slider.value = resetValue;
            slider.dispatchEvent(new Event('input', { bubbles: true }));
        });
        // Tab navigation
        // Keyboard shortcuts
        document.addEventListener('keydown', e => {
            // Only handle shortcuts when not typing in input fields
            if (e.target instanceof HTMLInputElement ||
                e.target instanceof HTMLTextAreaElement ||
                e.target instanceof HTMLSelectElement) {
                return;
            }
            // Every shortcut here is a bare letter, so anything with a modifier
            // belongs to the editor or the OS, not to us. Without this the handler
            // swallowed Cmd/Ctrl+C, +A, +S and +F - each one calling preventDefault
            // on a keystroke the user meant for copy, select-all, save or find.
            if (e.metaKey || e.ctrlKey || e.altKey) {
                return;
            }
            switch (e.key.toLowerCase()) {
                case 'h':
                    this.showKeyboardShortcuts();
                    e.preventDefault();
                    break;
                case 'f':
                    if (!this.sequenceMode) {
                        this.fitCameraToAllObjects();
                    }
                    e.preventDefault();
                    break;
                case 'r':
                    if (!this.sequenceMode) {
                        this.resetCameraToDefault();
                    }
                    e.preventDefault();
                    break;
                case 'a':
                    this.toggleAxesVisibility();
                    e.preventDefault();
                    break;
                // Deliberately not 'c': that key belongs to copying, and a viewer
                // shortcut sitting on it is a trap even with the modifier guard above.
                case 'v':
                    this.setOpenCVCameraConvention();
                    if (this.vscode) {
                        this.vscode.postMessage({ type: 'saveCameraConvention', convention: 'opencv' });
                    }
                    e.preventDefault();
                    break;
                case 'b':
                    this.setOpenGLCameraConvention();
                    if (this.vscode) {
                        this.vscode.postMessage({ type: 'saveCameraConvention', convention: 'opengl' });
                    }
                    e.preventDefault();
                    break;
                case 't':
                    this.switchToTrackballControls();
                    e.preventDefault();
                    break;
                case 'o':
                    this.switchToOrbitControls();
                    e.preventDefault();
                    break;
                case 'i':
                    this.switchToLegacyTrackballControls();
                    e.preventDefault();
                    break;
                case 'k':
                    this.switchToArcballControls();
                    e.preventDefault();
                    break;
                // Arcball settings bindings
                case 'x':
                    this.setUpVector(new THREE.Vector3(1, 0, 0));
                    e.preventDefault();
                    break;
                case 'y':
                    this.setUpVector(new THREE.Vector3(0, 1, 0));
                    e.preventDefault();
                    break;
                case 'z':
                    this.setUpVector(new THREE.Vector3(0, 0, 1));
                    e.preventDefault();
                    break;
                case 'w':
                    // debug
                    this.setRotationCenterToOrigin();
                    this.updateRotationOriginButtonState();
                    e.preventDefault();
                    break;
                case 'g':
                    this.toggleGammaCorrection();
                    e.preventDefault();
                    break;
                case 's':
                    this.toggleScreenSpaceScaling();
                    e.preventDefault();
                    break;
                case 'e':
                    this.toggleEDL();
                    e.preventDefault();
                    break;
                case 'u':
                    this.toggleTransparency();
                    e.preventDefault();
                    break;
                case 'l':
                    this.arcballInvertRotation = !this.arcballInvertRotation;
                    if (this.controlType === 'arcball') {
                        const arc = this.controls;
                        if (arc && typeof arc.invertRotation === 'boolean') {
                            arc.invertRotation = this.arcballInvertRotation;
                        }
                    }
                    this.showStatus(`Arcball handedness: ${this.arcballInvertRotation ? 'Inverted' : 'Normal'}`);
                    e.preventDefault();
                    break;
            }
        });
        // Depth control handlers are now handled per-file in updateFileList
        // Global color mode toggle (removed - now handled per file)
    }
    initializeSequence(files, wildcard) {
        sequencePlayback.initializeSequence(this, files, wildcard);
    }
    updateSequenceUI() {
        sequencePlayback.updateSequenceUI(this);
    }
    playSequence() {
        sequencePlayback.playSequence(this);
    }
    pauseSequence() {
        sequencePlayback.pauseSequence(this);
    }
    stopSequence() {
        sequencePlayback.stopSequence(this);
    }
    stepSequence(delta) {
        sequencePlayback.stepSequence(this, delta);
    }
    seekSequence(index) {
        sequencePlayback.seekSequence(this, index);
    }
    async sequenceHandleUltimate(message) {
        await sequencePlayback.sequenceHandleUltimate(this, message);
    }
    async sequenceHandlePly(message) {
        await sequencePlayback.sequenceHandlePly(this, message);
    }
    async sequenceHandleXyz(message) {
        await sequencePlayback.sequenceHandleXyz(this, message);
    }
    async sequenceHandleObj(message) {
        await sequencePlayback.sequenceHandleObj(this, message);
    }
    async sequenceHandleStl(message) {
        await sequencePlayback.sequenceHandleStl(this, message);
    }
    async sequenceHandleDepth(message) {
        await sequencePlayback.sequenceHandleDepth(this, message);
    }
    trimNormalModeArraysFrom(startIndex) {
        sequencePlayback.trimNormalModeArraysFrom(this, startIndex);
    }
    async loadSequenceFrame(index) {
        await sequencePlayback.loadSequenceFrame(this, index);
    }
    useSequenceObject(obj, index) {
        sequencePlayback.useSequenceObject(this, obj, index);
    }
    cacheSequenceOnly(obj, index) {
        sequencePlayback.cacheSequenceOnly(this, obj, index);
    }
    swapSequenceObject(obj, index) {
        sequencePlayback.swapSequenceObject(this, obj, index);
    }
    ensureSequenceVisibility(obj) {
        sequencePlayback.ensureSequenceVisibility(obj);
    }
    fitCameraToObject(obj) {
        const box = new THREE.Box3().setFromObject(obj);
        if (!box.isEmpty()) {
            const size = box.getSize(new THREE.Vector3());
            const center = box.getCenter(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);
            this.screenAnchor?.refreshAutomatic();
            const safeFraction = this.screenAnchor?.getFitFractions() ?? { width: 1, height: 1 };
            const vFov = this.camera.fov * (Math.PI / 180);
            const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
            const distance = Math.max(maxDim / (2 * Math.tan(vFov / 2) * safeFraction.height), maxDim / (2 * Math.tan(hFov / 2) * safeFraction.width)) * 1.5;
            // Move camera along its current direction to the new distance
            const dir = this.camera.getWorldDirection(new THREE.Vector3()).normalize();
            this.camera.position.copy(center.clone().sub(dir.multiplyScalar(distance)));
            applyFixedClipPlanes(this.camera);
            // Update controls target if present
            if (this.controls && this.controls.target) {
                this.controls.target.copy(center);
            }
        }
    }
    getDepthSettingsFromFileUI(fileIndex) {
        return depthPanelState.getDepthSettingsFromFileUI(fileIndex);
    }
    rebuildAllPlyMaterials() {
        for (let i = 0; i < this.meshes.length && i < this.spatialFiles.length; i++) {
            const data = this.spatialFiles[i];
            const mesh = this.meshes[i];
            if (!data || !mesh || data.sceneModel) {
                continue;
            }
            // Only update triangle meshes, not points or line segments
            const isTriangleMesh = mesh.type === 'Mesh' && !mesh.isLineSegments;
            if (!isTriangleMesh) {
                continue;
            }
            const oldMaterial = mesh.material;
            const newMaterial = this.createMaterialForFile(data, i);
            mesh.material = newMaterial;
            if (oldMaterial) {
                if (Array.isArray(oldMaterial)) {
                    oldMaterial.forEach(m => m.dispose());
                }
                else {
                    oldMaterial.dispose();
                }
            }
        }
        // Trigger a single render after material changes
        try {
            this.renderOnce?.();
        }
        catch { }
    }
    switchTab(tabName) {
        uiStatus.switchTab(tabName);
        // Tab collapse/expansion is intentional layout state, so it is one of the
        // few times the automatic visual center should follow the panel. Ordinary
        // row/content changes do not call this and therefore cannot move the view.
        requestAnimationFrame(() => this.screenAnchor?.refreshAutomatic());
    }
    toggleAxesVisibility() {
        axesFeature.toggleAxesVisibility(this);
    }
    toggleNormalsVisibility() {
        this.normalsVisualizers.forEach(normals => {
            if (normals) {
                normals.visible = !normals.visible;
            }
        });
        this.requestRender();
    }
    togglePointsVisibility(fileIndex) {
        if (fileIndex < 0 || fileIndex >= this.meshes.length) {
            return;
        }
        // Initialize visibility state if not set
        if (this.pointsVisible[fileIndex] === undefined) {
            this.pointsVisible[fileIndex] = true;
        }
        // Toggle the visibility state
        this.pointsVisible[fileIndex] = !this.pointsVisible[fileIndex];
        // Apply to the actual mesh
        if (this.meshes[fileIndex]) {
            this.meshes[fileIndex].visible = this.pointsVisible[fileIndex];
        }
        this.requestRender();
        // this.requestRender();
    }
    toggleFileNormalsVisibility(fileIndex) {
        if (fileIndex < 0 || fileIndex >= this.normalsVisualizers.length) {
            return;
        }
        // Initialize visibility state if not set
        if (this.normalsVisible[fileIndex] === undefined) {
            this.normalsVisible[fileIndex] = true;
        }
        // Toggle the visibility state
        this.normalsVisible[fileIndex] = !this.normalsVisible[fileIndex];
        // Apply to the actual normals visualizer
        if (this.normalsVisualizers[fileIndex]) {
            this.normalsVisualizers[fileIndex].visible = this.normalsVisible[fileIndex];
        }
        this.requestRender();
        // this.requestRender();
    }
    updatePointsNormalsButtonStates() {
        // Update points toggle button states
        const pointsButtons = document.querySelectorAll('.points-toggle-btn');
        pointsButtons.forEach(button => {
            const fileIndex = parseInt(button.getAttribute('data-file-index') || '0');
            const isVisible = this.pointsVisible[fileIndex] !== false; // Default to true
            const baseStyle = 'flex: 1; padding: 4px 8px; border: 1px solid var(--vscode-panel-border); border-radius: 2px; cursor: pointer; font-size: 10px;';
            if (isVisible) {
                button.setAttribute('style', baseStyle +
                    ' background: var(--vscode-button-background); color: var(--vscode-button-foreground);');
            }
            else {
                button.setAttribute('style', baseStyle +
                    ' background: var(--vscode-button-secondaryBackground); color: var(--vscode-button-secondaryForeground);');
            }
        });
        // Update normals toggle button states
        const normalsButtons = document.querySelectorAll('.normals-toggle-btn');
        normalsButtons.forEach(button => {
            const fileIndex = parseInt(button.getAttribute('data-file-index') || '0');
            // Skip disabled buttons (files without normals)
            if (button.hasAttribute('disabled') || button.classList.contains('disabled')) {
                return;
            }
            const isVisible = this.normalsVisible[fileIndex] !== false; // Default to true
            const baseStyle = 'flex: 1; padding: 4px 8px; border: 1px solid var(--vscode-panel-border); border-radius: 2px; cursor: pointer; font-size: 10px;';
            if (isVisible) {
                button.setAttribute('style', baseStyle +
                    ' background: var(--vscode-button-background); color: var(--vscode-button-foreground);');
            }
            else {
                button.setAttribute('style', baseStyle +
                    ' background: var(--vscode-button-secondaryBackground); color: var(--vscode-button-secondaryForeground);');
            }
        });
    }
    updateAxesButtonState() {
        axesFeature.updateAxesButtonState(this);
    }
    updateRotationOriginButtonState() {
        rotationCenterFeature.updateRotationOriginButtonState(this);
    }
    setUpVector(upVector) {
        axesFeature.setUpVector(this, upVector);
    }
    showUpVectorFeedback(upVector) {
        axesFeature.showUpVectorFeedback(this, upVector);
    }
    updateAxesForUpVector(upVector) {
        axesFeature.updateAxesForUpVector(this, upVector);
    }
    showUpVectorIndicator(upVector) {
        axesFeature.showUpVectorIndicator(this, upVector);
    }
    completeBackgroundOperation() {
        this.pendingFileLoads = Math.max(0, this.pendingFileLoads - 1);
        uiState.fileLoading = this.pendingFileLoads > 0;
    }
    showKeyboardShortcuts() {
        uiStatus.showKeyboardShortcuts(() => this.createShortcutsUI());
    }
    createShortcutsUI() {
        uiStatus.createShortcutsUI(() => this.updateControlStatus());
    }
    setupMessageHandler() {
        window.addEventListener('message', async (event) => {
            const message = event.data;
            const tracksBackgroundChange = BACKGROUND_CHANGE_MESSAGES.has(message.type);
            if (tracksBackgroundChange) {
                uiState.backgroundChanges++;
            }
            try {
                switch (message.type) {
                    case 'nativeAgentRequest':
                        await handleNativeAgentRequest(this, message);
                        break;
                    case 'registrationResult':
                        handleRegistrationExtensionResult(message);
                        break;
                    // Benchmark harness only (scripts/benchmark-vscode.mjs): runs one
                    // scripted step of the load → align → recolour scenario. See
                    // benchmarkScenario.ts for why this seam exists.
                    case 'benchmarkScenario':
                        await runBenchmarkScenario(this, message.step, message.anchorIndex ?? 0);
                        break;
                    case 'timing':
                        this.handleTimingMessage(message);
                        break;
                    case 'startLoading':
                        this.showImmediateLoading(message);
                        break;
                    case 'backgroundOperationComplete':
                        this.completeBackgroundOperation();
                        break;
                    case 'timingUpdate':
                        // Allow timing updates, suppress other spam
                        if (typeof message.message === 'string' &&
                            message.message.includes('🧪 Header face types')) {
                            console.log(message.message);
                        }
                        break;
                    case 'loadingError':
                        const fileType = message.fileType || 'point cloud';
                        const fileName = message.fileName ? ` (${message.fileName})` : '';
                        this.showError(`Failed to load ${fileType} file${fileName}: ${message.error}`);
                        break;
                    case 'stonexColorReady':
                        stationPipelineFeature.applyLoadTimeColors(this, message.updates ?? []);
                        break;
                    case 'stationPipelineProgress':
                        stationPipelineFeature.reportStationPipelineProgress(message.message);
                        break;
                    case 'stationPipelineResult':
                        stationPipelineFeature.handleStationPipelineResult(this, message);
                        break;
                    case 'spatialData':
                    case 'multiSpatialData':
                        try {
                            // Both single and multi-file data are handled the same way now
                            const dataArray = Array.isArray(message.data) ? message.data : [message.data];
                            await this.loadWithPerf('ply', message, () => this.displayFiles(dataArray));
                        }
                        catch (error) {
                            console.error('Error displaying PLY data:', error);
                            this.showError('Failed to display PLY data: ' +
                                (error instanceof Error ? error.message : String(error)));
                        }
                        break;
                    case 'sceneModelData':
                        await this.loadWithPerf('model', message, () => handleSceneModelMessage(this, message));
                        break;
                    case 'modelResourceResult':
                    case 'cadDecodeResult':
                        break;
                    case 'splatContainerUri':
                        await handleSplatContainerUri(this, message);
                        break;
                    case 'ultimateRawBinaryData':
                        try {
                            await this.handleUltimateRawBinaryData(message);
                        }
                        catch (error) {
                            console.error('Error handling ultimate raw binary data:', error);
                            this.showError('Failed to handle ultimate raw binary data: ' +
                                (error instanceof Error ? error.message : String(error)));
                        }
                        break;
                    case 'ultimateRawBinaryUri':
                        await this.handleUltimateRawBinaryUri(message);
                        break;
                    case 'directTypedArrayData':
                        try {
                            await this.loadWithPerf('ply', message, () => this.handleDirectTypedArrayData(message));
                        }
                        catch (error) {
                            console.error('Error handling direct TypedArray data:', error);
                            this.showError('Failed to handle direct TypedArray data: ' +
                                (error instanceof Error ? error.message : String(error)));
                        }
                        break;
                    case 'binarySpatialData':
                        try {
                            await this.loadWithPerf('ply', message, () => this.handleBinarySpatialData(message));
                            stationPipelineFeature.flushPendingLoadTimeColors(this);
                        }
                        catch (error) {
                            console.error('Error handling binary PLY data:', error);
                            this.showError('Failed to handle binary PLY data: ' +
                                (error instanceof Error ? error.message : String(error)));
                        }
                        break;
                    case 'addFiles':
                        try {
                            this.addNewFiles(message.data);
                        }
                        catch (error) {
                            console.error('Error adding new files:', error);
                            this.showError('Failed to add files: ' + (error instanceof Error ? error.message : String(error)));
                        }
                        break;
                    case 'sequence:init':
                        try {
                            this.initializeSequence(message.files, message.wildcard);
                        }
                        catch (error) {
                            console.error('Error starting sequence:', error);
                            this.showError('Failed to start sequence: ' +
                                (error instanceof Error ? error.message : String(error)));
                        }
                        break;
                    case 'sequence:file:ultimate':
                        await this.sequenceHandleUltimate(message);
                        break;
                    case 'sequence:file:ply':
                        await this.sequenceHandlePly(message);
                        break;
                    case 'sequence:file:xyz':
                        await this.sequenceHandleXyz(message);
                        break;
                    case 'sequence:file:obj':
                        await this.sequenceHandleObj(message);
                        break;
                    case 'sequence:file:stl':
                        await this.sequenceHandleStl(message);
                        break;
                    case 'sequence:file:depth':
                        await this.sequenceHandleDepth(message);
                        break;
                    case 'fileRemoved':
                        try {
                            this.removeFileByIndex(message.fileIndex);
                        }
                        catch (error) {
                            console.error('Error removing file:', error);
                            this.showError('Failed to remove file: ' + (error instanceof Error ? error.message : String(error)));
                        }
                        break;
                    case 'startLargeFile':
                        this.handleStartLargeFile(message);
                        break;
                    case 'largeFileChunk':
                        this.handleLargeFileChunk(message);
                        break;
                    case 'largeFileComplete':
                        await this.handleLargeFileComplete(message);
                        break;
                    case 'cancelLargeFile':
                        largeFileChunking.handleCancelLargeFile(this, message);
                        break;
                    case 'depthData':
                        this.handleDepthData(message);
                        break;
                    case 'objData':
                        await this.loadWithPerf('obj', message, () => this.handleObjData(message));
                        break;
                    case 'stlData':
                        await this.loadWithPerf('stl', message, () => this.handleStlData(message));
                        break;
                    case 'xyzData':
                        await this.loadWithPerf('xyz', message, () => this.handleXyzData(message));
                        break;
                    case 'pcdData':
                        await this.loadWithPerf('pcd', message, () => this.handlePcdData(message));
                        break;
                    case 'ptsData':
                        await this.loadWithPerf('pts', message, () => this.handlePtsData(message));
                        break;
                    case 'kittiBinData':
                        await this.loadWithPerf('kitti-bin', message, () => this.handleKittiBinData(message));
                        break;
                    case 'offData':
                        await this.loadWithPerf('off', message, () => this.handleOffData(message));
                        break;
                    case 'gltfData':
                        await this.loadWithPerf('gltf', message, () => this.handleGltfData(message));
                        break;
                    case 'volumeData':
                        if (!acceptVolumeResponse(message.data?.metadata?.volumeSessionId, message.requestId)) {
                            break;
                        }
                        updateVolumeProgress(message.data?.metadata?.volumeSessionId, message.requestId, 1);
                        await this.loadWithPerf('volume', message, () => this.handleVolumeData(message));
                        break;
                    case 'volume:progress':
                        updateVolumeProgress(message.sessionId, message.requestId, message.fraction);
                        break;
                    case 'volume:error':
                        setVolumeError(message.sessionId, message.requestId, message.error);
                        break;
                    case 'colmapModelFiles':
                        await this.loadWithPerf('colmap', message, () => this.handleColmapModelFiles(message));
                        break;
                    case 'colmapImages':
                        await this.handleColmapImages(message);
                        break;
                    case 'npyData':
                        await this.loadWithPerf('npy', message, () => this.handleNpyData(message));
                        break;
                    case 'xyzVariantData':
                        await this.loadWithPerf('xyz', message, () => this.handleXyzVariantData(message));
                        break;
                    case 'cameraParams':
                        this.handleCameraParams(message);
                        break;
                    case 'cameraParamsCancelled':
                        this.handleCameraParamsCancelled(message.requestId);
                        break;
                    case 'datasetTexture':
                        this.handleDatasetTexture(message);
                        break;
                    case 'cameraParamsError':
                        this.handleCameraParamsError(message.error, message.requestId);
                        break;
                    case 'savePlyFileResult':
                        this.handleSaveSpatialFileResult(message);
                        break;
                    case 'colorImageData':
                        this.handleColorImageData(message);
                        break;
                    case 'defaultDepthSettings':
                        this.handleDefaultDepthSettings(message);
                        break;
                    case 'mtlData':
                        this.handleMtlData(message);
                        break;
                    case 'calibrationFileSelected':
                        this.handleCalibrationFileSelected(message);
                        break;
                    case 'poseData':
                        try {
                            await this.handlePoseData(message);
                        }
                        catch (error) {
                            console.error('Error handling pose data:', error);
                            this.showError('Failed to handle pose data: ' +
                                (error instanceof Error ? error.message : String(error)));
                        }
                        break;
                }
            }
            finally {
                if (tracksBackgroundChange) {
                    uiState.backgroundChanges = Math.max(0, uiState.backgroundChanges - 1);
                }
            }
        });
    }
    handleTimingMessage(msg) {
        if (!this.currentTiming) {
            this.currentTiming = { kind: msg.kind };
        }
        if (msg.phase === 'start') {
            this.currentTiming = { kind: msg.kind, startAt: msg.at };
        }
        else if (msg.phase === 'read') {
            this.currentTiming = { ...(this.currentTiming || { kind: msg.kind }), readMs: msg.ms };
        }
        else if (msg.phase === 'parse') {
            this.currentTiming = {
                ...(this.currentTiming || { kind: msg.kind }),
                parseMs: msg.ms,
                format: msg.format || this.currentTiming?.format,
            };
        }
        else if (msg.phase === 'convert') {
            this.currentTiming = { ...(this.currentTiming || { kind: msg.kind }), convertMs: msg.ms };
        }
        else if (msg.phase === 'total') {
            this.currentTiming = {
                ...(this.currentTiming || { kind: msg.kind }),
                totalMs: msg.ms,
                startAt: this.currentTiming?.startAt || msg.at,
            };
            // Emit final summary line with exact timestamp
            const iso = msg.at ? new Date(msg.at).toISOString() : new Date().toISOString();
            const timeOnly = `${new Date(iso).toTimeString().split(' ')[0]}.${new Date(iso).getMilliseconds().toString().padStart(3, '0')}`;
            const kind = (this.currentTiming.kind || 'unknown').toUpperCase();
            const fmt = this.currentTiming.format ? `, format=${this.currentTiming.format}` : '';
            const read = typeof this.currentTiming.readMs === 'number'
                ? `read ${this.currentTiming.readMs}ms`
                : null;
            const parse = typeof this.currentTiming.parseMs === 'number'
                ? `parse ${this.currentTiming.parseMs}ms`
                : null;
            const convert = typeof this.currentTiming.convertMs === 'number'
                ? `convert ${this.currentTiming.convertMs}ms`
                : null;
            const render = this.lastGeometryMs ? `render ${this.lastGeometryMs}ms` : null;
            const parts = [read, parse, convert, render].filter(Boolean).join(', ');
            const totalAbs = this.lastAbsoluteMs
                ? this.lastAbsoluteMs.toFixed(1)
                : (this.currentTiming.totalMs ?? 0).toFixed(1);
            console.log(`[${timeOnly}] Summary: ${kind}${fmt} - ${parts} | total ${totalAbs}ms`);
            this.currentTiming = null;
        }
    }
    /**
     * Initialize browser file handler with shared functionality
     */
    initializeBrowserFileHandler() {
        browserFileDragDrop.initializeBrowserFileHandler(this);
    }
    /**
     * Handle messages in browser mode - implements VS Code extension functionality locally
     */
    handleBrowserMessage(message) {
        browserFileDragDrop.handleBrowserMessage(this, message);
    }
    // # VSCode changes: the functions below are used in the browser and were not used for the extension
    // Browser file handling methods
    setupPanelResizeAndDrag() {
        browserFileDragDrop.setupPanelResizeAndDrag();
    }
    setupBrowserFileHandlers() {
        browserFileDragDrop.setupBrowserFileHandlers(this);
    }
    handleDragOver(event) {
        browserFileDragDrop.handleDragOver(event);
    }
    async handleDropEvent(event) {
        await browserFileDragDrop.handleDropEvent(this, event);
    }
    extractDroppedFilePaths(dataTransfer) {
        return browserFileDragDrop.extractDroppedFilePaths(dataTransfer);
    }
    async handleDroppedFiles(files) {
        await browserFileDragDrop.handleDroppedFiles(this, files);
    }
    async handleBrowserFiles(files) {
        await browserFileDragDrop.handleBrowserFiles(this, files);
    }
    // # VSCode changes: the functions above are used in the browser and were not used for the extension
    async displayFiles(dataArray) {
        // concise summary printed separately
        // In sequence mode: do not auto-fit camera or heavy UI work
        if (this.sequenceMode) {
            this.addNewFiles(dataArray);
            // Capture and restore form states even in sequence mode
            const openPanelStates = this.captureDepthPanelStates();
            this.updateFileList();
            this.restoreDepthPanelStates(openPanelStates);
            // Ensure color consistency with current gamma setting
            this.rebuildAllColorAttributesForCurrentGammaSetting();
            uiState.loadingVisible = false;
            return;
        }
        // Normal mode
        this.addNewFiles(dataArray);
        this.updateFileStats();
        // Capture current form states before regenerating UI
        const openPanelStates = this.captureDepthPanelStates();
        this.updateFileList();
        // Restore form values after UI regeneration
        this.restoreDepthPanelStates(openPanelStates);
        this.updateCameraControlsPanel();
        // Ensure color consistency with current gamma setting
        this.rebuildAllColorAttributesForCurrentGammaSetting();
        this.autoFitCameraOnFirstLoad();
        this.showLoading(false);
        this.clearError();
        const absStart = window.absoluteStartTime || performance.now();
        this.lastAbsoluteMs = performance.now() - absStart;
    }
    async yieldToUI() {
        return new Promise(resolve => setTimeout(resolve, 0));
    }
    createMaterialForFile(data, fileIndex) {
        const colorMode = this.individualColorModes[fileIndex] || 'assigned';
        if (data.faceCount > 0) {
            // Mesh material
            const material = 
            // Slice planes and voxel boxes carry their presentation grey directly
            // in the vertex colours, so scene lighting must not tint them.
            this.useUnlitPly ||
                data.metadata?.volumeRenderMode === 'slices' ||
                data.metadata?.volumeRenderMode === 'voxels'
                ? new THREE.MeshBasicMaterial()
                : new THREE.MeshLambertMaterial();
            material.side = THREE.DoubleSide; // More robust visibility if face winding varies
            // For files without explicit normals, prefer flat shading to avoid odd gradients
            if (material instanceof THREE.MeshLambertMaterial) {
                material.flatShading = !data.hasNormals;
            }
            if (this.shouldUseVertexColors(data, colorMode)) {
                material.vertexColors = true;
                material.color = new THREE.Color(1, 1, 1); // White base color
            }
            else if (colorMode === 'assigned') {
                // Use assigned color
                const color = this.fileColors[fileIndex % this.fileColors.length];
                material.color.setRGB(color[0], color[1], color[2]);
            }
            else {
                // Use color index
                const colorIndex = parseInt(colorMode);
                if (!isNaN(colorIndex) && colorIndex >= 0 && colorIndex < this.fileColors.length) {
                    const color = this.fileColors[colorIndex];
                    material.color.setRGB(color[0], color[1], color[2]);
                }
            }
            material.needsUpdate = true;
            return material;
        }
        else {
            // Points material
            const material = new THREE.PointsMaterial();
            // Initialize point size if not set
            if (!this.pointSizes[fileIndex]) {
                this.pointSizes[fileIndex] = DEFAULT_POINT_SIZE;
                filesState.pointSizes[fileIndex] = DEFAULT_POINT_SIZE;
            }
            material.size = this.pointSizes[fileIndex];
            material.sizeAttenuation = true; // Always use distance-based scaling
            // Apply point count-based optimizations
            const pointCount = data.vertexCount || data.vertices?.length || 0;
            this.optimizeForPointCount(material, pointCount);
            // debug
            if (this.shouldUseVertexColors(data, colorMode)) {
                material.vertexColors = true;
                material.color = new THREE.Color(1, 1, 1); // White base color
            }
            else if (colorMode === 'assigned') {
                // Use assigned color
                const color = this.fileColors[fileIndex % this.fileColors.length];
                material.color.setRGB(color[0], color[1], color[2]);
            }
            else {
                // Use color index
                const colorIndex = parseInt(colorMode);
                if (!isNaN(colorIndex) && colorIndex >= 0 && colorIndex < this.fileColors.length) {
                    const color = this.fileColors[colorIndex];
                    material.color.setRGB(color[0], color[1], color[2]);
                }
            }
            // Point colors are stored as raw 8-bit sRGB; decode them in-shader when in
            // original mode with the gamma toggle on (visually identical to the old
            // baked-Float32 path, 4x less memory).
            this.setupPointSrgbDecode(material);
            material.userData.srgbDecode = this.pointColorsNeedSrgbDecode(data, colorMode);
            return material;
        }
    }
    fitCameraToAllObjects() {
        if (this.meshes.length === 0 &&
            this.poseGroups.length === 0 &&
            this.cameraGroups.length === 0) {
            return;
        }
        const box = new THREE.Box3();
        for (const obj of this.meshes) {
            box.expandByObject(obj);
        }
        for (const group of this.poseGroups) {
            box.expandByObject(group);
        }
        for (const group of this.cameraGroups) {
            if (group.userData.excludeFromFit) {
                continue;
            }
            box.expandByObject(group);
        }
        if (box.isEmpty()) {
            return;
        }
        // Structure-from-motion clouds carry a few badly triangulated points that
        // can stretch the box by an order of magnitude, which would park the orbit
        // pivot in empty space. When that has happened, frame what the points
        // actually occupy instead. Well-behaved clouds keep their previous
        // framing - see visualization/robustBounds.ts.
        const trimmed = new THREE.Box3();
        for (let index = 0; index < this.spatialFiles.length; index++) {
            const positions = this.spatialFiles[index]?.positionsArray;
            const mesh = this.meshes[index];
            if (!positions || !mesh) {
                continue;
            }
            const local = robustPointBounds(positions);
            if (local) {
                mesh.updateWorldMatrix(true, false);
                trimmed.union(local.applyMatrix4(mesh.matrixWorld));
            }
        }
        const fitBox = !trimmed.isEmpty() && boundsAreOutlierDominated(box, trimmed) ? trimmed : box;
        const size = fitBox.getSize(new THREE.Vector3());
        const center = fitBox.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z, 1e-6);
        // Fit against the unobstructed region selected by ScreenAnchorController,
        // not the full canvas hidden partly beneath the top-right menu.
        this.screenAnchor?.refreshAutomatic();
        const safeFraction = this.screenAnchor?.getFitFractions() ?? { width: 1, height: 1 };
        const vFov = this.camera.fov * (Math.PI / 180);
        const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
        const fitHeightDistance = maxDim / (2 * Math.tan(vFov / 2) * safeFraction.height);
        const fitWidthDistance = maxDim / (2 * Math.tan(hFov / 2) * safeFraction.width);
        const distance = Math.max(fitHeightDistance, fitWidthDistance) * 1.5; // padding
        // Keep current camera viewing direction and move along it.
        const direction = this.camera.getWorldDirection(new THREE.Vector3()).normalize();
        // A direction along the up axis leaves lookAt without a defined roll (the
        // world tips so X reads as up) and the trackball without a rotation basis.
        if (Math.abs(direction.dot(this.camera.up)) > 0.999) {
            direction.set(-1, -1, -1).normalize();
        }
        this.camera.position.copy(center.clone().sub(direction.multiplyScalar(distance)));
        this.camera.lookAt(center);
        applyFixedClipPlanes(this.camera);
        // Set rotation center to fitted center
        this.controls.target.copy(center);
        this.controls.update();
    }
    autoFitCameraOnFirstLoad() {
        // Only on the first file: adding one later must never move the user's view.
        if (!this.isFirstFileLoad) {
            return;
        }
        // Terrestrial scans centre the scanner capture point in the view and use
        // it as the orbit pivot.
        // Everything else is framed by its bounding box, with a Z-up camera for
        // the formats that specify a vertical axis.
        const capturePoint = scannerCapturePointFor(this.spatialFiles);
        if (!capturePoint || !applyScannerStartView(this, capturePoint, this.meshes)) {
            if (shouldOrientZUp(this.spatialFiles)) {
                applyZUpOrientation(this);
            }
            this.fitCameraToAllObjects();
        }
        this.isFirstFileLoad = false;
    }
    updateFileStats() {
        filesState.statsLoadingFileName = null;
        filesState.statsTick++;
    }
    updateFileList() {
        // Phase 3 (docs/SVELTE_MIGRATION_PLAN.md): FileList.svelte now owns all
        // rendering and event wiring for the file list, reading directly from
        // this.spatialFiles/poseGroups/cameraGroups (and the many depth/pose/
        // camera getter methods below) each time renderTick changes - mirroring
        // this method's old "regenerate everything from scratch" model without
        // needing every underlying field to be individually reactive.
        // A settings update may change row contents, but must never move the
        // user's place in the list. FileList keeps stable keyed rows; preserving
        // the container offset here is a second invariant at the one refresh
        // boundary all current and future settings use.
        const fileList = document.getElementById('file-list');
        const scrollTop = fileList?.scrollTop ?? 0;
        const scrollLeft = fileList?.scrollLeft ?? 0;
        filesState.renderTick++;
        // Force the Svelte re-render to apply synchronously so the button-state
        // sync calls below see the freshly rendered DOM, matching the old
        // synchronous innerHTML-then-listeners-then-button-states ordering.
        flushSync();
        if (fileList) {
            fileList.scrollTop = scrollTop;
            fileList.scrollLeft = scrollLeft;
        }
        this.updatePointsNormalsButtonStates();
        this.updateUniversalRenderButtonStates();
        this.updateDefaultButtonState();
    }
    isDepthDerivedFile(data) {
        return commentSettings.isDepthDerivedFile(data);
    }
    /** Also the entry point for code that switches a mode, not just the picker. */
    onFileColorModeChange(fileIndex, value) {
        if (this.spatialFiles[fileIndex]?.sceneModel) {
            return;
        }
        this.individualColorModes[fileIndex] = value;
        filesState.colorModes[fileIndex] = value;
        const isPose = fileIndex >= this.spatialFiles.length;
        if (isPose) {
            const poseIndex = fileIndex - this.spatialFiles.length;
            const group = this.poseGroups[poseIndex];
            if (group) {
                const colorIdx = value === 'assigned' ? fileIndex % this.fileColors.length : parseInt(value);
                const color = isNaN(colorIdx)
                    ? this.fileColors[fileIndex % this.fileColors.length]
                    : this.fileColors[colorIdx];
                group.traverse(obj => {
                    if (obj.isInstancedMesh && obj instanceof THREE.InstancedMesh) {
                        const material = obj.material;
                        material.color.setRGB(color[0], color[1], color[2]);
                        material.needsUpdate = true;
                    }
                    else if (obj.isLineSegments && obj instanceof THREE.LineSegments) {
                        const material = obj.material;
                        material.color.setRGB(color[0], color[1], color[2]);
                        material.needsUpdate = true;
                    }
                });
            }
        }
        else if (fileIndex < this.meshes.length) {
            const mesh = this.meshes[fileIndex];
            const geometry = mesh.geometry;
            if (geometry) {
                this.applyColorModeToGeometry(this.spatialFiles[fileIndex], geometry, value);
            }
            const oldMaterial = this.meshes[fileIndex].material;
            const newMaterial = this.createMaterialForFile(this.spatialFiles[fileIndex], fileIndex);
            this.meshes[fileIndex].material = newMaterial;
            if (oldMaterial) {
                if (Array.isArray(oldMaterial)) {
                    oldMaterial.forEach((m) => m.dispose());
                }
                else {
                    oldMaterial.dispose();
                }
            }
        }
        // The renderer draws on demand, so a colour change that does not ask for a
        // frame is invisible until something else does — a camera nudge, a resize.
        // Switching a cloud from its projected colour to a flat one looked like a
        // control that did nothing at all.
        this.requestRender();
    }
    toggleFileVisibility(fileIndex) {
        if (fileIndex < 0) {
            return;
        }
        // Determine desired visibility from checkbox state
        const checkboxEl = document.getElementById(`file-${fileIndex}`);
        const desiredVisible = checkboxEl
            ? !!checkboxEl.checked
            : !(this.fileVisibility[fileIndex] ?? true);
        this.setFileEntryVisibility(fileIndex, desiredVisible);
    }
    /** Used by the capture-place toggles, which move several entries at once. */
    setFileEntryVisibility(fileIndex, desiredVisible) {
        this.fileVisibility[fileIndex] = desiredVisible;
        filesState.visibility[fileIndex] = desiredVisible;
        // If it's a mesh/pointcloud entry
        if (fileIndex < this.meshes.length && this.meshes[fileIndex]) {
            // Use the unified function to properly handle all visibility logic
            this.updateMeshVisibilityAndMaterial(fileIndex);
            // Also update normals visualizer visibility
            if (fileIndex < this.normalsVisualizers.length && this.normalsVisualizers[fileIndex]) {
                const normalsVisible = this.normalsVisible[fileIndex] ?? false;
                this.normalsVisualizers[fileIndex].visible = normalsVisible && desiredVisible;
            }
            return;
        }
        // Pose entries are appended after meshes
        const poseIndex = fileIndex - this.spatialFiles.length;
        if (poseIndex >= 0 && poseIndex < this.poseGroups.length) {
            const group = this.poseGroups[poseIndex];
            if (group) {
                group.visible = desiredVisible;
            }
            const labels = this.poseLabelsGroups[poseIndex];
            if (labels) {
                labels.visible = desiredVisible;
            }
            return;
        }
        // Camera entries are appended after poses
        const cameraIndex = fileIndex - this.spatialFiles.length - this.poseGroups.length;
        if (cameraIndex >= 0 && cameraIndex < this.cameraGroups.length) {
            const group = this.cameraGroups[cameraIndex];
            if (group) {
                group.visible = desiredVisible;
            }
        }
    }
    /**
     * Universal render mode toggle for all file types
     * Handles solid, wireframe, points, and normals rendering modes
     */
    toggleUniversalRenderMode(fileIndex, mode) {
        renderModeToggles.toggleUniversalRenderMode(this, fileIndex, mode);
        filesState.renderModeTick += 1;
    }
    toggleSolidRendering(fileIndex) {
        renderModeToggles.toggleSolidRendering(this, fileIndex);
    }
    toggleWireframeRendering(fileIndex) {
        renderModeToggles.toggleWireframeRendering(this, fileIndex);
    }
    togglePointsRendering(fileIndex) {
        renderModeToggles.togglePointsRendering(this, fileIndex);
    }
    updateMeshVisibilityAndMaterial(fileIndex) {
        renderModeToggles.updateMeshVisibilityAndMaterial(this, fileIndex);
    }
    updateVertexPointsVisualization(fileIndex, pointsVisible, solidVisible, wireframeVisible, fileVisible) {
        renderModeToggles.updateVertexPointsVisualization(this, fileIndex, pointsVisible, solidVisible, wireframeVisible, fileVisible);
    }
    createVertexPointsFromMesh(mesh, fileIndex) {
        return renderModeToggles.createVertexPointsFromMesh(this, mesh, fileIndex);
    }
    updateMultiMaterialPointsVisualization(fileIndex, pointsVisible, fileVisible) {
        renderModeToggles.updateMultiMaterialPointsVisualization(this, fileIndex, pointsVisible, fileVisible);
    }
    toggleNormalsRendering(fileIndex) {
        renderModeToggles.toggleNormalsRendering(this, fileIndex);
    }
    updateUniversalRenderButtonStates() {
        renderModeToggles.updateUniversalRenderButtonStates(this);
    }
    showImmediateLoading(message) {
        const fileName = message.fileName;
        const uiStartTime = performance.now();
        console.log(`Load: UI start ${fileName} at ${uiStartTime.toFixed(1)}ms`);
        this.isFileLoading = true;
        if (this.runningInVSCode) {
            this.pendingFileLoads++;
        }
        uiState.fileLoading = true;
        this.updateWelcomeMessageVisibility();
        // Store timing for complete analysis
        window.loadingStartTime = uiStartTime;
        window.absoluteStartTime = uiStartTime;
        // Additional load into an existing scene: don't cover the cloud with a
        // blocking spinner — the user can keep looking at / interacting with the
        // current clouds. Show progress as a row in the Files list instead.
        if (this.spatialFiles.length > 0) {
            this.pendingLoadLabel = fileName;
            this.pendingLoadDetail = 'Reading file…';
            uiState.loadingVisible = false;
            // Wrap in capture/restore like every other updateFileList() call site -
            // otherwise this remount silently discards any depth settings panel
            // that's open and edited (open state + unsaved field values) the
            // moment a second file starts loading.
            const openPanelStates = this.captureDepthPanelStates();
            this.updateFileList();
            this.restoreDepthPanelStates(openPanelStates);
            return;
        }
        // First/empty load: nothing to interact with yet, so a centered spinner is
        // appropriate. Show the real filename + a live phase line.
        uiState.loadingVisible = true;
        uiState.loadingTitle = `Loading ${fileName}…`;
        uiState.loadingDetail = 'Reading file…';
        // Show the main UI elements immediately (before file loads)
        const infoPanelEl = document.getElementById('info-panel');
        if (infoPanelEl) {
            infoPanelEl.style.visibility = 'visible';
        }
        const viewerContainerEl = document.getElementById('viewer-container');
        if (viewerContainerEl) {
            viewerContainerEl.style.visibility = 'visible';
        }
        // Keep the Files tab active for all files (depth controls are in Files tab)
        // Update file stats with placeholder
        this.updateFileStatsImmediate(fileName);
    }
    /**
     * Update the live phase line during a load — the centered spinner's detail for
     * a first load, or the in-progress Files-list row for an additional load.
     */
    setLoadingDetail(text) {
        if (!this.isFileLoading) {
            return;
        }
        if (this.pendingLoadLabel !== null) {
            this.pendingLoadDetail = text;
            const detailEl = document.getElementById('pending-load-detail');
            if (detailEl) {
                detailEl.textContent = text;
            }
            return;
        }
        uiState.loadingDetail = text;
    }
    updateFileStatsImmediate(fileName) {
        filesState.statsLoadingFileName = fileName;
    }
    showError(message) {
        uiStatus.showError(message);
    }
    clearError() {
        uiStatus.clearError();
    }
    // File management methods
    requestAddFile() {
        this.vscode.postMessage({
            type: 'addFile',
        });
    }
    requestRemoveFile(fileIndex) {
        this.vscode.postMessage({
            type: 'removeFile',
            fileIndex: fileIndex,
        });
    }
    requestLoadMtl(fileIndex) {
        this.vscode.postMessage({
            type: 'loadMtl',
            fileIndex: fileIndex,
        });
    }
    requestColorImageForDepth(fileIndex) {
        colorImageForDepth.requestColorImageForDepth(this, fileIndex);
    }
    addNewFiles(newFiles) {
        // Phase feedback: the bytes are here, now we build GPU geometry.
        if (this.isFileLoading && newFiles.length > 0) {
            const pts = newFiles.reduce((s, f) => s + (f.vertexCount || 0), 0);
            this.setLoadingDetail(pts > 0 ? `Building geometry (${pts.toLocaleString()} points)…` : 'Building geometry…');
        }
        const stonexCameraFiles = [];
        const e57CameraFiles = [];
        const colmapCameraFiles = [];
        for (const data of newFiles) {
            alignSourceOrigin(data, this.spatialFiles);
            // Assign new file index
            data.fileIndex = this.spatialFiles.length;
            // Add to data array
            this.spatialFiles.push(data);
            // Claim the entry's slot before anything writes per-file state into it.
            // Spatial files sort first, so the unified index equals data.fileIndex,
            // but the insert still has to happen here: appending would land past any
            // pose or camera entries already loaded and hand this file their slots.
            const { index: entryIndex } = this.fileEntries.add('spatial');
            insertEntryState(this, entryIndex, {
                visible: true,
                pointSize: DEFAULT_POINT_SIZE,
                colorMode: 'assigned',
            });
            // Update welcome message visibility
            this.updateWelcomeMessageVisibility();
            // Initialize visibility states based on file type
            const isObjFile = data.isObjFile;
            const objData = data.objData;
            const isMultiMaterial = isObjFile && objData && objData.materialGroups && objData.materialGroups.length > 1;
            if (data.faceCount > 0 || data.sceneModel) {
                // Native scenes can contain point primitives as well as meshes.
                this.solidVisible.push(true);
                if (isMultiMaterial) {
                    // Multi-material OBJ - points represent distinct geometric elements
                    this.pointsVisible.push(true); // Show points by default
                }
                else {
                    // Single-material mesh - points are just mesh vertices
                    this.pointsVisible.push(false); // Don't show mesh vertices as points
                }
            }
            else {
                // Point cloud file (PLY, XYZ, PTS) - show points only
                this.solidVisible.push(false); // No mesh surface exists
                this.pointsVisible.push(true); // Show actual point data
            }
            // Wireframe and normals always start disabled
            this.wireframeVisible.push(false);
            this.normalsVisible.push(false);
            // Initialize vertex points object (null initially, created on demand)
            this.vertexPointsObjects.push(null);
            // Initialize color mode before creating material. The slot already exists
            // at entryIndex, so this only assigns.
            const initialColorMode = (data.metadata?.volumeRenderMode === 'slices' ||
                data.metadata?.volumeRenderMode === 'points' ||
                data.metadata?.volumeRenderMode === 'voxels') &&
                data.hasColors
                ? 'original'
                : this.useOriginalColors && data.hasColors
                    ? 'original'
                    : this.hasIntensityData(data)
                        ? 'intensity'
                        : 'assigned';
            this.individualColorModes[data.fileIndex] = initialColorMode;
            filesState.colorModes[data.fileIndex] = initialColorMode;
            console.log(`🎨 addNewFiles - fileIndex: ${data.fileIndex}, hasColors: ${data.hasColors}, colorMode: ${initialColorMode}, useOriginalColors: ${this.useOriginalColors}`);
            const recommendedPointSize = data.metadata?.recommendedPointSize;
            const initialPointSize = typeof recommendedPointSize === 'number' && recommendedPointSize > 0
                ? recommendedPointSize
                : DEFAULT_POINT_SIZE;
            this.pointSizes[data.fileIndex] = initialPointSize;
            filesState.pointSizes[data.fileIndex] = initialPointSize;
            // debug
            // Create geometry and material
            // Use data.fileIndex which is the spatialFiles array index
            const geometry = this.createGeometryFromSpatialData(data);
            const material = this.createMaterialForFile(data, data.fileIndex);
            // Check if this is an OBJ file and handle different rendering modes
            const isObjFile2 = data.isObjFile;
            const objRenderType = data.objRenderType;
            if (data.sceneModel) {
                const modelObject = createModelObject(data, geometry, material);
                this.scene.add(modelObject);
                this.meshes.push(modelObject);
                this.requestRender();
            }
            else if (isObjFile2) {
                if (objRenderType === 'wireframe' && data.objLines) {
                    // Create wireframe using LineSegments
                    const lines = data.objLines;
                    const linePositions = new Float32Array(lines.length * 6); // 2 vertices per line, 3 coords per vertex
                    for (let i = 0; i < lines.length; i++) {
                        const line = lines[i];
                        const startVertex = data.vertices[line.start];
                        const endVertex = data.vertices[line.end];
                        const i6 = i * 6;
                        linePositions[i6] = startVertex.x;
                        linePositions[i6 + 1] = startVertex.y;
                        linePositions[i6 + 2] = startVertex.z;
                        linePositions[i6 + 3] = endVertex.x;
                        linePositions[i6 + 4] = endVertex.y;
                        linePositions[i6 + 5] = endVertex.z;
                    }
                    const lineGeometry = new THREE.BufferGeometry();
                    lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
                    const lineMaterial = new THREE.LineBasicMaterial({
                        color: 0xff0000, // Red wireframe
                    });
                    const wireframeMesh = new THREE.LineSegments(lineGeometry, lineMaterial);
                    wireframeMesh.isLineSegments = true;
                    this.scene.add(wireframeMesh);
                    this.meshes.push(wireframeMesh);
                    this.requestRender();
                }
                else if (objRenderType === 'mesh' && data.faceCount > 0) {
                    // Create multi-material mesh(es)
                    const objData = data.objData;
                    if (objData && objData.materialGroups && objData.materialGroups.length > 1) {
                        // Multi-material rendering: create separate mesh for each material group
                        const subMeshes = [];
                        const meshGroup = new THREE.Group();
                        for (const materialGroup of objData.materialGroups) {
                            if (materialGroup.faces.length > 0) {
                                // Create geometry for this material group
                                const groupGeometry = new THREE.BufferGeometry();
                                // Collect vertices for faces in this group
                                const faceVertices = [];
                                const faceIndices = [];
                                let vertexOffset = 0;
                                for (const face of materialGroup.faces) {
                                    if (face.indices.length >= 3) {
                                        // Add vertices for this face
                                        for (const vertexIndex of face.indices) {
                                            const vertex = data.vertices[vertexIndex];
                                            faceVertices.push(vertex.x, vertex.y, vertex.z);
                                        }
                                        // Triangulate face (fan triangulation)
                                        for (let i = 1; i < face.indices.length - 1; i++) {
                                            faceIndices.push(vertexOffset);
                                            faceIndices.push(vertexOffset + i);
                                            faceIndices.push(vertexOffset + i + 1);
                                        }
                                        vertexOffset += face.indices.length;
                                    }
                                }
                                if (faceVertices.length > 0) {
                                    groupGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(faceVertices), 3));
                                    groupGeometry.setIndex(faceIndices);
                                    groupGeometry.computeVertexNormals();
                                    const groupMaterial = new THREE.MeshBasicMaterial({
                                        color: 0x808080, // Default gray - will be colored by MTL
                                        side: THREE.DoubleSide,
                                    });
                                    const groupMesh = new THREE.Mesh(groupGeometry, groupMaterial);
                                    groupMesh.materialName = materialGroup.material;
                                    groupMesh.isObjMesh = true;
                                    meshGroup.add(groupMesh);
                                    subMeshes.push(groupMesh);
                                }
                            }
                            // Handle lines in this material group
                            if (materialGroup.lines.length > 0) {
                                const linePositions = new Float32Array(materialGroup.lines.length * 6);
                                for (let i = 0; i < materialGroup.lines.length; i++) {
                                    const line = materialGroup.lines[i];
                                    const startVertex = data.vertices[line.start];
                                    const endVertex = data.vertices[line.end];
                                    const i6 = i * 6;
                                    linePositions[i6] = startVertex.x;
                                    linePositions[i6 + 1] = startVertex.y;
                                    linePositions[i6 + 2] = startVertex.z;
                                    linePositions[i6 + 3] = endVertex.x;
                                    linePositions[i6 + 4] = endVertex.y;
                                    linePositions[i6 + 5] = endVertex.z;
                                }
                                const lineGeometry = new THREE.BufferGeometry();
                                lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
                                const lineMaterial = new THREE.LineBasicMaterial({
                                    color: 0xff0000, // Default red - will be colored by MTL
                                });
                                const lineSegments = new THREE.LineSegments(lineGeometry, lineMaterial);
                                lineSegments.materialName = materialGroup.material;
                                lineSegments.isLineSegments = true;
                                meshGroup.add(lineSegments);
                                subMeshes.push(lineSegments);
                            }
                            // Handle points in this material group
                            if (materialGroup.points.length > 0) {
                                const pointPositions = new Float32Array(materialGroup.points.length * 3);
                                for (let i = 0; i < materialGroup.points.length; i++) {
                                    const point = materialGroup.points[i];
                                    const vertex = data.vertices[point.index];
                                    const i3 = i * 3;
                                    pointPositions[i3] = vertex.x;
                                    pointPositions[i3 + 1] = vertex.y;
                                    pointPositions[i3 + 2] = vertex.z;
                                }
                                const pointGeometry = new THREE.BufferGeometry();
                                pointGeometry.setAttribute('position', new THREE.BufferAttribute(pointPositions, 3));
                                const pointMaterial = new THREE.PointsMaterial({
                                    color: 0xff0000, // Default red - will be colored by MTL
                                    size: this.pointSizes[data.fileIndex] || 0.001, // Use stored point size (world units)
                                    sizeAttenuation: true, // Use world-space sizing like other file types
                                    // Apply transparency settings
                                    transparent: this.allowTransparency,
                                    alphaTest: this.allowTransparency ? 0.1 : 0,
                                    opacity: 1.0,
                                    depthWrite: true,
                                    depthTest: true,
                                    side: THREE.DoubleSide,
                                });
                                const points = new THREE.Points(pointGeometry, pointMaterial);
                                points.materialName = materialGroup.material;
                                points.isPoints = true;
                                meshGroup.add(points);
                                subMeshes.push(points);
                            }
                        }
                        meshGroup.isObjMesh = true;
                        meshGroup.isMultiMaterial = true;
                        this.scene.add(meshGroup);
                        this.multiMaterialGroups[data.fileIndex] = meshGroup;
                        this.materialMeshes[data.fileIndex] = subMeshes;
                        console.log(`Created multi-material OBJ with ${subMeshes.length} sub-meshes`);
                    }
                    else {
                        // Single material or fallback to original logic
                        const meshMaterial = new THREE.MeshBasicMaterial({
                            color: 0x808080,
                            side: THREE.DoubleSide,
                            vertexColors: data.hasColors,
                        });
                        if (objData && objData.hasNormals && objData.normals.length > 0) {
                            const normals = new Float32Array(data.vertexCount * 3);
                            for (let i = 0; i < data.vertexCount && i < objData.normals.length; i++) {
                                const normal = objData.normals[i];
                                normals[i * 3] = normal.nx;
                                normals[i * 3 + 1] = normal.ny;
                                normals[i * 3 + 2] = normal.nz;
                            }
                            geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
                        }
                        const mesh = new THREE.Mesh(geometry, meshMaterial);
                        mesh.isObjMesh = true;
                        this.scene.add(mesh);
                        this.meshes.push(mesh);
                        this.requestRender();
                        // this.requestRender();
                    }
                }
                else {
                    // Fallback to points - use optimized creation
                    const mesh = this.createOptimizedPointCloud(geometry, material);
                    this.scene.add(mesh);
                    this.meshes.push(mesh);
                    this.requestRender();
                }
            }
            else {
                // Handle legacy OBJ wireframe format and regular PLY files
                const isObjWireframe = data.isObjWireframe;
                if (isObjWireframe && data.objLines) {
                    // Legacy wireframe handling
                    const lines = data.objLines;
                    const linePositions = new Float32Array(lines.length * 6);
                    for (let i = 0; i < lines.length; i++) {
                        const line = lines[i];
                        const startVertex = data.vertices[line.start];
                        const endVertex = data.vertices[line.end];
                        const i6 = i * 6;
                        linePositions[i6] = startVertex.x;
                        linePositions[i6 + 1] = startVertex.y;
                        linePositions[i6 + 2] = startVertex.z;
                        linePositions[i6 + 3] = endVertex.x;
                        linePositions[i6 + 4] = endVertex.y;
                        linePositions[i6 + 5] = endVertex.z;
                    }
                    const lineGeometry = new THREE.BufferGeometry();
                    lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
                    const lineMaterial = new THREE.LineBasicMaterial({
                        color: 0xff0000,
                    });
                    const wireframeMesh = new THREE.LineSegments(lineGeometry, lineMaterial);
                    wireframeMesh.isLineSegments = true;
                    this.scene.add(wireframeMesh);
                    this.meshes.push(wireframeMesh);
                    this.requestRender();
                }
                else {
                    // Create regular mesh for PLY files
                    const shouldShowAsPoints = data.faceCount === 0;
                    const mesh = shouldShowAsPoints
                        ? this.createOptimizedPointCloud(geometry, material)
                        : new THREE.Mesh(geometry, material);
                    this.scene.add(mesh);
                    this.meshes.push(mesh);
                    this.requestRender();
                }
            }
            // If sequence mode is active, only the current frame stays visible to avoid overloading the scene
            const isSeqMode = this.sequenceFiles.length > 0;
            const shouldBeVisible = !isSeqMode || data.fileIndex === this.sequenceIndex;
            this.fileVisibility[data.fileIndex] = shouldBeVisible;
            filesState.visibility[data.fileIndex] = shouldBeVisible;
            const lastObject = this.meshes[this.meshes.length - 1];
            if (lastObject) {
                lastObject.visible = shouldBeVisible;
            }
            const isObjFile3 = data.isObjFile;
            this.appliedMtlColors.push(null); // No MTL color applied initially
            this.appliedMtlNames.push(null); // No MTL material applied initially
            this.appliedMtlData.push(null); // No MTL data applied initially
            this.multiMaterialGroups.push(null); // No multi-material group initially
            this.materialMeshes.push(null); // No sub-meshes initially
            if (data.metadata?.stonexCameraFrames?.length) {
                stonexCameraFiles.push(data);
            }
            if (data.metadata?.e57Images?.length) {
                e57CameraFiles.push(data);
            }
            if (data.metadata?.colmapModel) {
                colmapCameraFiles.push(data);
            }
        }
        for (const data of stonexCameraFiles) {
            try {
                stonexCameras.addStonexCameraVisualization(this, data);
            }
            catch (error) {
                // Camera helpers are auxiliary. A malformed preview or calibration must
                // never prevent the point cloud from being fitted and rendered.
                console.error(`Could not add Stonex camera visualization for ${data.fileName}:`, error);
            }
        }
        for (const data of colmapCameraFiles) {
            try {
                addColmapCameraVisualization(this, data);
            }
            catch (error) {
                console.error(`Could not add COLMAP camera visualization for ${data.fileName}:`, error);
            }
        }
        for (const data of e57CameraFiles) {
            try {
                addE57CameraVisualization(this, data);
            }
            catch (error) {
                console.error(`Could not add E57 camera visualization for ${data.fileName}:`, error);
            }
        }
        // Update UI (preserve depth panel states)
        const openPanelStates = this.captureDepthPanelStates();
        this.updateFileList();
        this.restoreDepthPanelStates(openPanelStates);
        this.updateFileStats();
        this.showLoading(false);
        // Splat containers (.spz/.splat/…) render as splats right away.
        this.splatMode.autoEnablePending();
    }
    /**
     * Drops an entry's registry slot and per-entry state, together with any
     * entry that declared it as a parent.
     *
     * A container publishes children - a COLMAP reconstruction's cloud owns its
     * camera profile - and removing the parent alone would strand their scene
     * objects with no row to control them.
     */
    removeEntryAndChildren(fileIndex) {
        const entry = this.fileEntries.at(fileIndex);
        // Collection positions must be read before the registry forgets them.
        const children = entry
            ? this.fileEntries.childrenOf(entry.id).map(child => {
                const unified = this.fileEntries.indexOf(child.id);
                return { kind: child.kind, kindIndex: this.fileEntries.kindIndexAt(unified) };
            })
            : [];
        // Only camera profiles are published as children today. Highest index
        // first so the remaining positions stay valid.
        const cameraChildren = children
            .filter(child => child.kind === 'camera')
            .sort((a, b) => b.kindIndex - a.kindIndex);
        for (const child of cameraChildren) {
            const group = this.cameraGroups[child.kindIndex];
            if (!group) {
                continue;
            }
            this.scene.remove(group);
            group.traverse((object) => {
                object.geometry?.dispose?.();
                const materials = Array.isArray(object.material) ? object.material : [object.material];
                for (const material of materials) {
                    material?.map?.dispose?.();
                    material?.dispose?.();
                }
            });
            this.cameraGroups.splice(child.kindIndex, 1);
            this.cameraNames.splice(child.kindIndex, 1);
            this.cameraShowLabels.splice(child.kindIndex, 1);
            this.cameraShowCoords.splice(child.kindIndex, 1);
        }
        // The registry returns removals in descending index order, so these
        // per-entry splices stay valid as they go.
        for (const removal of this.fileEntries.removeAt(fileIndex)) {
            removeEntryState(this, removal.index);
        }
    }
    removeFileByIndex(fileIndex) {
        if (fileIndex < 0) {
            return;
        }
        // Which collection this index refers to is the registry's to answer.
        const kind = this.fileEntries.kindAt(fileIndex);
        if (kind === 'camera') {
            const cameraIndex = this.fileEntries.kindIndexAt(fileIndex);
            if (cameraIndex < 0 || cameraIndex >= this.cameraGroups.length) {
                return;
            }
            const group = this.cameraGroups[cameraIndex];
            this.scene.remove(group);
            group.traverse((obj) => {
                if (obj.geometry && typeof obj.geometry.dispose === 'function') {
                    obj.geometry.dispose();
                }
                if (obj.material) {
                    if (Array.isArray(obj.material)) {
                        obj.material.forEach((m) => m.dispose && m.dispose());
                    }
                    else if (typeof obj.material.dispose === 'function') {
                        const material = obj.material;
                        material.map?.dispose();
                        obj.material.dispose();
                    }
                }
            });
            this.cameraGroups.splice(cameraIndex, 1);
            this.cameraNames.splice(cameraIndex, 1);
            this.cameraShowLabels.splice(cameraIndex, 1);
            this.cameraShowCoords.splice(cameraIndex, 1);
            this.removeEntryAndChildren(fileIndex);
            // Preserve depth panel states when removing files
            const openPanelStates = this.captureDepthPanelStates();
            this.updateFileList();
            this.restoreDepthPanelStates(openPanelStates);
            this.updateFileStats();
            return;
        }
        if (kind === 'pose') {
            const poseIndex = this.fileEntries.kindIndexAt(fileIndex);
            if (poseIndex < 0 || poseIndex >= this.poseGroups.length) {
                return;
            }
            const group = this.poseGroups[poseIndex];
            this.scene.remove(group);
            group.traverse((obj) => {
                if (obj.geometry && typeof obj.geometry.dispose === 'function') {
                    obj.geometry.dispose();
                }
                if (obj.material) {
                    if (Array.isArray(obj.material)) {
                        obj.material.forEach((m) => m.dispose && m.dispose());
                    }
                    else if (typeof obj.material.dispose === 'function') {
                        obj.material.dispose();
                    }
                }
            });
            this.poseGroups.splice(poseIndex, 1);
            this.poseMeta.splice(poseIndex, 1);
            this.poseLabelsGroups.splice(poseIndex, 1);
            this.poseJoints.splice(poseIndex, 1);
            this.poseEdges.splice(poseIndex, 1);
            // pose.ts keys its per-pose toggles by the unified index, not by the pose
            // index, so they are spliced here alongside the shared entry state rather
            // than with poseGroups above.
            this.poseUseDatasetColors.splice(fileIndex, 1);
            this.poseShowLabels.splice(fileIndex, 1);
            this.poseScaleByScore.splice(fileIndex, 1);
            this.poseScaleByUncertainty.splice(fileIndex, 1);
            this.poseConvention.splice(fileIndex, 1);
            this.poseMinScoreThreshold.splice(fileIndex, 1);
            this.poseMaxUncertaintyThreshold.splice(fileIndex, 1);
            this.removeEntryAndChildren(fileIndex);
            // Preserve depth panel states when removing files
            const openPanelStates = this.captureDepthPanelStates();
            this.updateFileList();
            this.restoreDepthPanelStates(openPanelStates);
            this.updateFileStats();
            return;
        }
        this.spatialFiles[fileIndex]?.sceneModel?.dispose();
        // Remove mesh from scene
        const mesh = this.meshes[fileIndex];
        this.scene.remove(mesh);
        if (mesh.geometry) {
            mesh.geometry.dispose();
        }
        if (mesh.material) {
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach(mat => mat.dispose());
            }
            else {
                mesh.material.dispose();
            }
        }
        // Remove normals visualizer from scene and dispose
        const normalsVisualizer = this.normalsVisualizers[fileIndex];
        if (normalsVisualizer) {
            this.scene.remove(normalsVisualizer);
            if (normalsVisualizer.geometry) {
                normalsVisualizer.geometry.dispose();
            }
            if (normalsVisualizer.material) {
                if (Array.isArray(normalsVisualizer.material)) {
                    normalsVisualizer.material.forEach(mat => mat.dispose());
                }
                else {
                    normalsVisualizer.material.dispose();
                }
            }
        }
        // Remove vertex points object from scene and dispose
        const vertexPoints = this.vertexPointsObjects[fileIndex];
        if (vertexPoints) {
            this.scene.remove(vertexPoints);
            if (vertexPoints.geometry) {
                vertexPoints.geometry.dispose();
            }
            if (vertexPoints.material) {
                if (Array.isArray(vertexPoints.material)) {
                    vertexPoints.material.forEach(mat => mat.dispose());
                }
                else {
                    vertexPoints.material.dispose();
                }
            }
        }
        // Remove multi-material group from scene and dispose
        const multiMaterialGroup = this.multiMaterialGroups[fileIndex];
        if (multiMaterialGroup) {
            this.scene.remove(multiMaterialGroup);
            multiMaterialGroup.traverse((obj) => {
                if (obj.geometry && typeof obj.geometry.dispose === 'function') {
                    obj.geometry.dispose();
                }
                if (obj.material) {
                    if (Array.isArray(obj.material)) {
                        obj.material.forEach((m) => m.dispose && m.dispose());
                    }
                    else if (typeof obj.material.dispose === 'function') {
                        obj.material.dispose();
                    }
                }
            });
        }
        // Remove from arrays
        const volumeSessionId = this.spatialFiles[fileIndex]?.metadata?.volumeSessionId;
        if (typeof volumeSessionId === 'string') {
            const source = this.volumeSources.get(volumeSessionId);
            if (source) {
                source.generation++;
            }
            this.volumeSources.delete(volumeSessionId);
        }
        this.splatMode.onFileRemoved(fileIndex);
        this.splatModeActive.splice(fileIndex, 1);
        this.spatialFiles.splice(fileIndex, 1);
        this.sectionPlanes.onFileRemoved(fileIndex);
        this.meshes.splice(fileIndex, 1);
        this.normalsVisualizers.splice(fileIndex, 1); // Remove normals visualizer for this file
        this.vertexPointsObjects.splice(fileIndex, 1); // Remove vertex points object for this file
        this.multiMaterialGroups.splice(fileIndex, 1); // Remove multi-material group for this file
        this.materialMeshes.splice(fileIndex, 1); // Remove sub-meshes for this file
        this.removeEntryAndChildren(fileIndex);
        this.appliedMtlColors.splice(fileIndex, 1); // Remove MTL color for this file
        this.appliedMtlNames.splice(fileIndex, 1); // Remove MTL name for this file
        this.appliedMtlData.splice(fileIndex, 1); // Remove MTL data for this file
        // Remove rendering mode states for this file
        this.solidVisible.splice(fileIndex, 1);
        this.wireframeVisible.splice(fileIndex, 1);
        this.pointsVisible.splice(fileIndex, 1);
        this.normalsVisible.splice(fileIndex, 1);
        // Remove Depth data if it exists for this file
        this.fileDepthData.delete(fileIndex);
        // Update Depth data indices for remaining files (shift down)
        const newdepthData = new Map();
        for (const [key, value] of this.fileDepthData) {
            if (key > fileIndex) {
                newdepthData.set(key - 1, value);
            }
            else if (key < fileIndex) {
                newdepthData.set(key, value);
            }
        }
        this.fileDepthData = newdepthData;
        const shiftLiveDepthSet = (source) => {
            const next = new Set();
            for (const key of source) {
                if (key > fileIndex) {
                    next.add(key - 1);
                }
                else if (key < fileIndex) {
                    next.add(key);
                }
            }
            return next;
        };
        const removedTimer = this.liveDepthUpdateTimers.get(fileIndex);
        if (removedTimer !== undefined) {
            window.clearTimeout(removedTimer);
        }
        const shiftedTimers = new Map();
        for (const [key, timer] of this.liveDepthUpdateTimers) {
            if (key > fileIndex) {
                shiftedTimers.set(key - 1, timer);
            }
            else if (key < fileIndex) {
                shiftedTimers.set(key, timer);
            }
        }
        this.liveDepthUpdateTimers = shiftedTimers;
        this.liveDepthUpdateFiles = shiftLiveDepthSet(this.liveDepthUpdateFiles);
        this.liveDepthUpdateInFlight = shiftLiveDepthSet(this.liveDepthUpdateInFlight);
        this.liveDepthUpdateQueued = shiftLiveDepthSet(this.liveDepthUpdateQueued);
        const shiftedVersions = new Map();
        for (const [key, version] of this.liveDepthUpdateVersions) {
            if (key > fileIndex) {
                shiftedVersions.set(key - 1, version);
            }
            else if (key < fileIndex) {
                shiftedVersions.set(key, version);
            }
        }
        this.liveDepthUpdateVersions = shiftedVersions;
        // Reassign file indices
        for (let i = 0; i < this.spatialFiles.length; i++) {
            this.spatialFiles[i].fileIndex = i;
        }
        // Update UI (preserve depth panel states)
        const openPanelStates = this.captureDepthPanelStates();
        this.updateFileList();
        this.restoreDepthPanelStates(openPanelStates);
        this.updateFileStats();
        this.updateWelcomeMessageVisibility();
        // If all scene objects are gone, allow first-load auto-fit for the next import.
        if (this.spatialFiles.length === 0 &&
            this.poseGroups.length === 0 &&
            this.cameraGroups.length === 0) {
            this.isFirstFileLoad = true;
        }
        // Request render to update the display after removing objects
        this.requestRender();
    }
    /**
     * Generic timing wrapper for format handlers that don't self-report detailed
     * phases (OBJ, STL, PCD, PTS, OFF, GLTF, NPY, XYZ, ...). Captures the
     * cross-process transfer cost plus the handler (parse + geometry + display)
     * as a single comparable PERF line. Errors propagate; the summary still
     * fires via finally so failed loads are still timed.
     */
    async loadWithPerf(kind, message, fn) {
        await binaryDataHandlers.loadWithPerf(kind, message, fn);
    }
    /**
     * Transfer-via-fetch entry: instead of receiving the vertex buffer over
     * postMessage (a multi-hundred-ms structured clone for large clouds), fetch
     * the file directly from its webview URI, slice out the vertex bytes, and
     * hand off to the normal parser. On any fetch failure, ask the extension to
     * resend over postMessage (the proven path) so loading never breaks.
     */
    async handleUltimateRawBinaryUri(message) {
        await binaryDataHandlers.handleUltimateRawBinaryUri(this, message);
    }
    async handleUltimateRawBinaryData(message, preparsed) {
        await binaryDataHandlers.handleUltimateRawBinaryData(this, message, preparsed);
    }
    async handleDirectTypedArrayData(message) {
        await binaryDataHandlers.handleDirectTypedArrayData(this, message);
    }
    async handleBinarySpatialData(message) {
        await binaryDataHandlers.handleBinarySpatialData(this, message);
    }
    handleStartLargeFile(message) {
        largeFileChunking.handleStartLargeFile(this, message);
    }
    handleLargeFileChunk(message) {
        largeFileChunking.handleLargeFileChunk(this, message);
    }
    async handleLargeFileComplete(message) {
        await largeFileChunking.handleLargeFileComplete(this, message);
        stationPipelineFeature.flushPendingLoadTimeColors(this);
    }
    /** Called from the file rows and from the all-clouds slider in the controls. */
    updatePointSize(fileIndex, newSize) {
        pointSizeScaling.updatePointSize(this, fileIndex, newSize);
    }
    getColorName(fileIndex) {
        return colorModeUtils.getColorName(fileIndex);
    }
    getColorOptions(fileIndex) {
        return colorModeUtils.getColorOptions(this, fileIndex);
    }
    // ===== Pose feature updaters =====
    updatePoseAppearance(fileIndex) {
        pose.updatePoseAppearance(this, fileIndex);
    }
    updatePoseLabels(fileIndex) {
        pose.updatePoseLabels(this, fileIndex);
    }
    updatePoseScaling(fileIndex) {
        pose.updatePoseScaling(this, fileIndex);
    }
    applyPoseConvention(fileIndex, conv) {
        pose.applyPoseConvention(this, fileIndex, conv);
    }
    applyPoseFilters(fileIndex) {
        pose.applyPoseFilters(this, fileIndex);
    }
    soloPointCloud(fileIndex) {
        const totalEntries = this.fileEntries.length;
        const selectedIsSoleVisible = (this.fileVisibility[fileIndex] ?? filesState.visibility[fileIndex] ?? true) &&
            Array.from({ length: totalEntries }, (_, index) => index).every(index => index === fileIndex ||
                !(this.fileVisibility[index] ?? filesState.visibility[index] ?? true));
        const showAll = selectedIsSoleVisible;
        for (let i = 0; i < totalEntries; i++) {
            this.setFileEntryVisibility(i, showAll || i === fileIndex);
        }
        // filesState.visibility is reactive, so rebuilding the complete file list
        // here is unnecessary. Keeping the existing rows also preserves scroll,
        // focus, expanded panels, and each row's identity during Shift-click solo.
        this.requestRender();
    }
    switchToTrackballControls() {
        controlSchemeSwitcher.switchToTrackballControls(this);
    }
    switchToOrbitControls() {
        controlSchemeSwitcher.switchToOrbitControls(this);
    }
    switchToLegacyTrackballControls() {
        controlSchemeSwitcher.switchToLegacyTrackballControls(this);
    }
    switchToArcballControls() {
        controlSchemeSwitcher.switchToArcballControls(this);
    }
    updateControlStatus() {
        controlSchemeSwitcher.updateControlStatus(this);
    }
    setOpenCVCameraConvention() {
        cameraConvention.setOpenCVCameraConvention(this);
    }
    setOpenGLCameraConvention() {
        cameraConvention.setOpenGLCameraConvention(this);
    }
    loadMeasurementPathProject(jsonText) {
        return this.measurementManager?.loadPathProject(jsonText) ?? false;
    }
    updateAxesForCameraConvention(convention) {
        cameraConvention.updateAxesForCameraConvention(this, convention);
    }
    showCameraConventionFeedback(convention) {
        cameraConvention.showCameraConventionFeedback(this, convention);
    }
    showTranslationDialog(fileIndex) {
        transformDialogs.showTranslationDialog(this, fileIndex);
    }
    showQuaternionDialog(fileIndex) {
        transformDialogs.showQuaternionDialog(this, fileIndex);
    }
    showAngleAxisDialog(fileIndex) {
        transformDialogs.showAngleAxisDialog(this, fileIndex);
    }
    showCameraPositionDialog() {
        transformDialogs.showCameraPositionDialog(this);
    }
    showCameraRotationDialog() {
        transformDialogs.showCameraRotationDialog(this);
    }
    showRotationCenterDialog() {
        transformDialogs.showRotationCenterDialog(this);
    }
    openCalibrationFileDialog(fileIndex) {
        calibrationForm.openCalibrationFileDialog(this, fileIndex);
    }
    async loadCalibrationFile(file, fileIndex) {
        await calibrationForm.loadCalibrationFile(this, file, fileIndex);
    }
    displayCalibrationInfo(calibrationData, fileName, fileIndex) {
        calibrationForm.displayCalibrationInfo(this, calibrationData, fileName, fileIndex);
    }
    onCameraSelectionChange(fileIndex, selectedCamera) {
        calibrationForm.onCameraSelectionChange(this, fileIndex, selectedCamera);
    }
    handleCalibrationFileSelected(message) {
        calibrationForm.handleCalibrationFileSelected(this, message);
    }
    populateFormFromCalibration(cameraData, fileIndex) {
        calibrationForm.populateFormFromCalibration(this, cameraData, fileIndex);
    }
    async handleDepthData(message) {
        await depthConversionPipeline.handleDepthData(this, message);
    }
    /**
     * Show depth conversion UI for local parameter collection
     */
    async showDepthConversionUI(fileName, requestId) {
        await depthConversionPipeline.showDepthConversionUI(this, fileName, requestId);
    }
    /**
     * Process depth data using default camera parameters
     */
    async processDepthWithDefaults(fileName, data, requestId) {
        await depthConversionPipeline.processDepthWithDefaults(this, fileName, data, requestId);
    }
    async processDepthWithParams(requestId, cameraParams) {
        await depthConversionPipeline.processDepthWithParams(this, requestId, cameraParams);
    }
    async processDepthToPointCloud(depthData, fileName, cameraParams, colorImageData) {
        return this.depthWorkerClient.processDepthToPointCloud(depthData, fileName, cameraParams, colorImageData);
    }
    async handleObjData(message) {
        await formatDataHandlers.handleObjData(this, message);
    }
    async handleStlData(message) {
        await formatDataHandlers.handleStlData(this, message);
    }
    async handleXyzData(message) {
        await formatDataHandlers.handleXyzData(this, message);
    }
    async handleCameraParams(message) {
        await depthCameraParamsPrompt.handleCameraParams(this, message);
    }
    saveCameraParams(params) {
        depthCameraParamsPrompt.saveCameraParams(params);
    }
    handleCameraParamsCancelled(requestId) {
        depthCameraParamsPrompt.handleCameraParamsCancelled(this, requestId);
    }
    handleCameraParamsError(error, requestId) {
        depthCameraParamsPrompt.handleCameraParamsError(this, error, requestId);
    }
    handleSaveSpatialFileResult(message) {
        if (message.success) {
            this.showStatus(`PLY file saved successfully: ${message.filePath}`);
            console.log(`✅ PLY file saved: ${message.filePath}`);
        }
        else {
            if (message.cancelled) {
                this.showStatus('Save operation cancelled by user');
            }
            else {
                this.showError(`Failed to save PLY file: ${message.error || 'Unknown error'}`);
                console.error('PLY save error:', message.error);
            }
        }
    }
    async handlePcdData(message) {
        await formatDataHandlers.handlePcdData(this, message);
    }
    async handleNpyData(message) {
        await formatDataHandlers.handleNpyData(this, message);
    }
    /**
     * A COLMAP sparse model arriving from the extension host as a set of raw
     * files. The extension cannot parse them one at a time - the model only makes
     * sense as a whole - so it forwards the bytes and the assembly happens here,
     * the same way the standalone page does it.
     */
    async handleColmapModelFiles(message) {
        const files = (message.files || []).map((file) => ({ name: file.name, data: messageBytes(file.data) }));
        // An empty part means the transfer dropped the payload rather than that the
        // model is empty; saying "no points3D" here would send you looking in the
        // wrong place entirely.
        const empty = files.filter(file => file.data.byteLength === 0).map(file => file.name);
        if (empty.length > 0) {
            this.showError(`COLMAP model files arrived empty: ${empty.join(', ')}`);
            return;
        }
        const collected = collectColmapModelFiles(files);
        if (!collected) {
            const found = files.map(file => file.name).join(', ') || 'none';
            this.showError(`COLMAP model needs a cameras and an images file. Found: ${found}.`);
            return;
        }
        const model = parseColmapModel(collected.model);
        const cloud = buildSparseCloud(model, colmapReconstructionName(files));
        if (!cloud) {
            this.showError(collected.model.points3D
                ? 'COLMAP points3D file contained no points.'
                : 'COLMAP model has no points3D file; load it alongside cameras and images to see the sparse cloud.');
            return;
        }
        cloud.metadata = { ...cloud.metadata, colmapModel: model };
        // Read back by loadWithPerf's summary, so the PERF line for the
        // reconstruction reports the cloud it actually built.
        message.vertexCount = cloud.vertexCount;
        await this.displayFiles([cloud]);
        // The frames are on screen now; photographs arrive separately and attach
        // themselves as they decode.
        this.colmapImageNames = model.images.map(image => image.name);
        this.colmapImagesDone = 0;
    }
    /**
     * A batch of reconstruction photographs. Each is decoded and hung on its own
     * frame as soon as it is ready, so the frames fill in progressively instead
     * of the whole set appearing at the end.
     */
    async handleColmapImages(message) {
        const profile = this.cameraGroups.find(group => group.name.startsWith('colmap_cameras_'));
        if (!profile || this.colmapImageNames.length === 0) {
            return;
        }
        const total = message.total ?? 0;
        const sources = (message.files || []).map((file) => ({
            name: file.name,
            data: messageBytes(file.data),
        }));
        let attached = false;
        await loadColmapTextures(sources, this.colmapImageNames, (name, texture) => {
            if (attachColmapFrameImage(profile, name, texture)) {
                attached = true;
            }
            this.colmapImagesDone += 1;
            profile.userData.imageProgress = { done: this.colmapImagesDone, total };
        });
        if (attached) {
            // The panel only offers the image toggle once a frame actually has one.
            profile.userData.hasImagePlanes = true;
            this.requestRender();
        }
        filesState.renderTick += 1;
        if (total > 0 && this.colmapImagesDone >= total) {
            // One PERF line for the whole background phase, separate from the
            // reconstruction's own line, so the two costs can be read apart.
            const perf = new PerfTimer('colmap-images', message.loadStartedAt);
            perf.mark('read+decode');
            // Not `verts`: that note is formatted as "pts", which would read as a
            // point count rather than an image count.
            perf.note('images', total);
            if (typeof message.totalBytes === 'number') {
                perf.note('MB', (message.totalBytes / 1048576).toFixed(1));
            }
            perf.summary();
            // Clearing it removes the counter; the "Show images" toggle stays.
            profile.userData.imageProgress = null;
            filesState.renderTick += 1;
        }
    }
    async handlePtsData(message) {
        await formatDataHandlers.handlePtsData(this, message);
    }
    async handleKittiBinData(message) {
        await formatDataHandlers.handleKittiBinData(this, message);
    }
    async handleOffData(message) {
        await formatDataHandlers.handleOffData(this, message);
    }
    async handleGltfData(message) {
        await formatDataHandlers.handleGltfData(this, message);
    }
    async handleVolumeData(message) {
        this.retainVolumeSource(message.data);
        await formatDataHandlers.handleVolumeData(this, message);
    }
    retainVolumeSource(data) {
        const metadata = data?.metadata;
        const sessionId = metadata?.volumeSessionId;
        const sizes = metadata?.volumeSizes;
        const samples = data?.intensityArray;
        if (typeof sessionId !== 'string' ||
            !Array.isArray(sizes) ||
            sizes.length !== 3 ||
            !samples ||
            samples.length !== sizes[0] * sizes[1] * sizes[2]) {
            return;
        }
        this.volumeSources.set(sessionId, {
            volume: {
                sizes: [...sizes],
                samples,
                ijkToWorld: [...metadata.ijkToWorld],
                spaceUnits: metadata.spaceUnits || 'm',
                intensityUnits: metadata.intensityUnits,
                range: metadata.volumeRange,
                channels: Number(metadata.channels) || 1,
                header: {
                    'photometric interpretation': metadata.photometricInterpretation || 'MONOCHROME2',
                },
                fileName: data.fileName,
            },
            metadata: { ...metadata },
            generation: 0,
        });
    }
    async reextractVolumeLocally(request) {
        const source = this.volumeSources.get(request.sessionId);
        if (!source) {
            // Older/partial volume payloads can still use the extension-host fallback.
            this.vscode.postMessage({ type: 'volume:reextract', ...request });
            return;
        }
        const generation = ++source.generation;
        const cancelled = () => generation !== source.generation;
        const onProgress = (fraction) => updateVolumeProgress(request.sessionId, request.requestId, fraction);
        try {
            const result = request.renderMode === 'slices'
                ? await buildVolumeSlicesAsync(source.volume, {
                    windowCenter: request.windowCenter,
                    windowWidth: request.windowWidth,
                    brightnessMode: request.brightnessMode,
                    volumeRange: source.metadata.volumeRange,
                    sliceRanges: source.metadata.volumeSliceRanges,
                    slices: request.sliceIndices,
                    onProgress,
                }, cancelled)
                : request.renderMode === 'mesh'
                    ? await buildVolumeMeshAsync(source.volume, { threshold: request.threshold, step: request.step, onProgress }, cancelled)
                    : request.renderMode === 'voxels'
                        ? await buildVolumeVoxelsAsync(source.volume, {
                            threshold: request.threshold,
                            step: [1, 1, 1],
                            clip: request.clipRanges,
                            windowCenter: request.windowCenter,
                            windowWidth: request.windowWidth,
                            brightnessMode: request.brightnessMode,
                            volumeRange: source.metadata.volumeRange,
                            sliceRanges: source.metadata.volumeSliceRanges,
                            onProgress,
                        }, cancelled)
                        : await buildVolumePointsAsync(source.volume, {
                            threshold: request.threshold,
                            step: [1, 1, 1],
                            windowCenter: request.windowCenter,
                            windowWidth: request.windowWidth,
                            brightnessMode: request.brightnessMode,
                            volumeRange: source.metadata.volumeRange,
                            sliceRanges: source.metadata.volumeSliceRanges,
                            onProgress,
                        }, cancelled);
            if (!result || cancelled()) {
                return;
            }
            result.data.metadata = {
                ...source.metadata,
                ...result.data.metadata,
                volumeSessionId: request.sessionId,
                volumeRenderMode: request.renderMode,
                threshold: request.threshold,
                windowCenter: request.windowCenter,
                windowWidth: request.windowWidth,
                brightnessMode: request.brightnessMode,
                meshExtractionStep: request.step,
                sliceIndices: request.sliceIndices,
            };
            updateVolumeProgress(request.sessionId, request.requestId, 1);
            await this.handleVolumeData({
                data: result.data,
                fileName: source.volume.fileName,
                replaceFileIndex: request.fileIndex,
                requestId: request.requestId,
            });
        }
        catch (error) {
            if (!cancelled()) {
                setVolumeError(request.sessionId, request.requestId, error instanceof Error ? error.message : String(error));
            }
        }
    }
    async handleXyzVariantData(message) {
        await formatDataHandlers.handleXyzVariantData(this, message);
    }
    createNormalsVisualizer(data) {
        return normalsVisualizer.createNormalsVisualizer(data);
    }
    createComputedNormalsVisualizer(data, mesh) {
        return normalsVisualizer.createComputedNormalsVisualizer(data, mesh);
    }
    createPointCloudNormalsVisualizer(data, mesh) {
        return normalsVisualizer.createPointCloudNormalsVisualizer(data, mesh);
    }
    async handleColorImageData(message) {
        await colorImageForDepth.handleColorImageData(this, message);
    }
    /**
     * Convert depth image to 3D point cloud
     * Based on the Python reference implementation
     */
    showStatus(message) {
        uiStatus.showStatus(message);
    }
    /**
     * Show color mapping status message
     */
    showColorMappingStatus(message, type) {
        uiStatus.showColorMappingStatus(message, type);
    }
    /**
     * Determine if a Depth image is a depth image suitable for point cloud conversion
     * Accepts both floating-point and integer formats (for disparity images)
     */
    isDepthTifImage(samplesPerPixel, sampleFormat, bitsPerSample) {
        // Depth images should be single-channel
        if (samplesPerPixel !== 1) {
            return false;
        }
        // Accept floating-point formats (sampleFormat 3) for depth images
        // and integer formats (sampleFormat 1, 2) for disparity images
        if (sampleFormat !== null && sampleFormat !== 1 && sampleFormat !== 2 && sampleFormat !== 3) {
            return false;
        }
        // If bit depth information is available, validate it
        if (bitsPerSample && bitsPerSample.length > 0 && bitsPerSample[0] !== undefined) {
            const bitDepth = bitsPerSample[0];
            // Accept common bit depths for depth/disparity images
            if (bitDepth !== 8 && bitDepth !== 16 && bitDepth !== 32) {
                return false;
            }
        }
        console.log(`✅ TIF validated as depth/disparity image: samples=${samplesPerPixel}, format=${sampleFormat}, bits=${bitsPerSample?.[0]}`);
        return true;
    }
    getRgb24ScaleFactor(data) {
        return commentSettings.getRgb24ScaleFactor(data);
    }
    getRgb24ConversionMode(data) {
        return commentSettings.getRgb24ConversionMode(data);
    }
    getPngScaleFactor(data) {
        return commentSettings.getPngScaleFactor(data);
    }
    getDepthSetting(data, setting) {
        return commentSettings.getDepthSetting(this, data, setting);
    }
    getDepthFx(data) {
        return commentSettings.getDepthFx(this, data);
    }
    getDepthFy(data) {
        return commentSettings.getDepthFy(this, data);
    }
    getDepthBaseline(data) {
        return commentSettings.getDepthBaseline(this, data);
    }
    getDepthCx(data, fileIndex) {
        return commentSettings.getDepthCx(this, data, fileIndex);
    }
    getDepthCy(data, fileIndex) {
        return commentSettings.getDepthCy(this, data, fileIndex);
    }
    getDepthConvention(data) {
        return commentSettings.getDepthConvention(this, data);
    }
    getStoredColorImageName(fileIndex) {
        return colorImageForDepth.getStoredColorImageName(this, fileIndex);
    }
    getImageSizeDisplay(fileIndex) {
        return colorImageForDepth.getImageSizeDisplay(this, fileIndex);
    }
    setLiveDepthUpdateEnabled(fileIndex, enabled) {
        liveDepthUpdate.setLiveDepthUpdateEnabled(this, fileIndex, enabled);
    }
    isDepthCommitTarget(target) {
        return liveDepthUpdate.isDepthCommitTarget(target);
    }
    scheduleLiveDepthUpdate(fileIndex, delayMs = 60) {
        liveDepthUpdate.scheduleLiveDepthUpdate(this, fileIndex, delayMs);
    }
    async requestLiveDepthUpdate(fileIndex) {
        await liveDepthUpdate.requestLiveDepthUpdate(this, fileIndex);
    }
    isLiveDepthResultCurrent(fileIndex, version) {
        return liveDepthUpdate.isLiveDepthResultCurrent(this, fileIndex, version);
    }
    waitForNextFrame() {
        return liveDepthUpdate.waitForNextFrame();
    }
    async applyDepthSettings(fileIndex, liveVersion) {
        await liveDepthUpdate.applyDepthSettings(this, fileIndex, liveVersion);
    }
    handleDefaultDepthSettings(message) {
        console.log('📥 Received default depth settings message:', message);
        if (message.settings) {
            // Update default settings from extension storage (exclude cx and cy as they are auto-calculated per image)
            this.defaultDepthSettings = {
                fx: message.settings.fx || 1000,
                fy: message.settings.fy,
                cx: this.defaultDepthSettings.cx, // Keep existing cx, don't load from storage
                cy: this.defaultDepthSettings.cy, // Keep existing cy, don't load from storage
                cameraModel: message.settings.cameraModel || 'pinhole-opencv',
                depthType: message.settings.depthType || 'euclidean',
                baseline: message.settings.baseline,
                convention: message.settings.convention || 'opengl',
                pngScaleFactor: message.settings.pngScaleFactor || 1000,
                depthScale: message.settings.depthScale !== undefined ? message.settings.depthScale : 1.0,
                depthBias: message.settings.depthBias !== undefined ? message.settings.depthBias : 0.0,
            };
            console.log('✅ Loaded default depth settings from extension:', this.defaultDepthSettings);
            // X3A/X3R and E57 define Z as world-up. The settings response is
            // asynchronous and can arrive after their first camera fit, so applying
            // a persisted generic OpenCV/OpenGL view here would deterministically
            // tip the survey onto its side. A response received before geometry is
            // still safe: the first fit establishes the format-defined up axis.
            if (shouldApplySavedViewConvention(this.spatialFiles)) {
                if (message.viewConvention === 'opencv') {
                    this.setOpenCVCameraConvention();
                }
                else if (message.viewConvention === 'opengl') {
                    this.setOpenGLCameraConvention();
                }
            }
            // Update any existing depth file forms to use new defaults
            this.refreshDepthFileFormsWithDefaults();
            this.updateDefaultButtonState();
        }
        else {
            console.log('⚠️ No settings in default depth settings message');
        }
    }
    refreshDepthFileFormsWithDefaults() {
        // Update existing depth file forms to use the new default settings
        for (let i = 0; i < this.spatialFiles.length; i++) {
            const data = this.spatialFiles[i];
            if (commentSettings.isDepthDerivedFile(data)) {
                console.log(`🔄 Refreshing depth form ${i} with new defaults`);
                this.updateDepthFormWithDefaults(i);
            }
        }
    }
    updateDepthFormWithDefaults(fileIndex) {
        // Update form fields to show default values (but preserve cx/cy if they exist from image dimensions)
        const fxInput = document.getElementById(`fx-${fileIndex}`);
        const fyInput = document.getElementById(`fy-${fileIndex}`);
        if (fxInput) {
            fxInput.value = this.defaultDepthSettings.fx.toString();
        }
        if (fyInput && this.defaultDepthSettings.fy !== undefined) {
            fyInput.value = this.defaultDepthSettings.fy.toString();
        }
        // Preserve cx/cy values if they were auto-calculated from Depth dimensions
        const cxInput = document.getElementById(`cx-${fileIndex}`);
        const cyInput = document.getElementById(`cy-${fileIndex}`);
        const depthData = this.fileDepthData.get(fileIndex);
        if (cxInput && depthData?.depthDimensions) {
            // Keep the computed cx value based on actual image dimensions
            const computedCx = (depthData.depthDimensions.width - 1) / 2;
            cxInput.value = computedCx.toString();
            console.log(`📐 Preserving computed cx = ${computedCx} for file ${fileIndex} (not overriding with defaults)`);
        }
        if (cyInput && depthData?.depthDimensions) {
            // Keep the computed cy value based on actual image dimensions
            const computedCy = (depthData.depthDimensions.height - 1) / 2;
            cyInput.value = computedCy.toString();
            console.log(`📐 Preserving computed cy = ${computedCy} for file ${fileIndex} (not overriding with defaults)`);
        }
        const cameraModelSelect = document.getElementById(`camera-model-${fileIndex}`);
        if (cameraModelSelect) {
            cameraModelSelect.value = this.defaultDepthSettings.cameraModel;
        }
        const depthTypeSelect = document.getElementById(`depth-type-${fileIndex}`);
        if (depthTypeSelect) {
            depthTypeSelect.value = this.defaultDepthSettings.depthType;
            // Update baseline and disparity offset visibility based on depth type
            const baselineGroup = document.getElementById(`baseline-group-${fileIndex}`);
            const disparityOffsetGroup = document.getElementById(`disparity-offset-group-${fileIndex}`);
            const isDisparity = this.defaultDepthSettings.depthType === 'disparity';
            if (baselineGroup) {
                baselineGroup.style.display = isDisparity ? '' : 'none';
            }
            if (disparityOffsetGroup) {
                disparityOffsetGroup.style.display = isDisparity ? '' : 'none';
            }
        }
        const baselineInput = document.getElementById(`baseline-${fileIndex}`);
        if (baselineInput && this.defaultDepthSettings.baseline !== undefined) {
            baselineInput.value = this.defaultDepthSettings.baseline.toString();
        }
        const conventionSelect = document.getElementById(`convention-${fileIndex}`);
        if (conventionSelect) {
            conventionSelect.value = this.defaultDepthSettings.convention || 'opengl';
        }
        const depthScaleInput = document.getElementById(`depth-scale-${fileIndex}`);
        if (depthScaleInput && this.defaultDepthSettings.depthScale !== undefined) {
            depthScaleInput.value = this.defaultDepthSettings.depthScale.toString();
        }
        const depthBiasInput = document.getElementById(`depth-bias-${fileIndex}`);
        if (depthBiasInput && this.defaultDepthSettings.depthBias !== undefined) {
            depthBiasInput.value = this.defaultDepthSettings.depthBias.toString();
        }
        console.log(`✅ Updated depth form ${fileIndex} with defaults:`, this.defaultDepthSettings);
    }
    updatePrinciplePointFields(fileIndex, dimensions) {
        // Update cx and cy form fields with computed values based on actual image dimensions
        const cxInput = document.getElementById(`cx-${fileIndex}`);
        const cyInput = document.getElementById(`cy-${fileIndex}`);
        const computedCx = (dimensions.width - 1) / 2;
        const computedCy = (dimensions.height - 1) / 2;
        if (cxInput) {
            cxInput.value = computedCx.toString();
        }
        if (cyInput) {
            cyInput.value = computedCy.toString();
        }
        // Update image size display
        const imageSizeDiv = document.getElementById(`image-size-${fileIndex}`);
        if (imageSizeDiv) {
            imageSizeDiv.textContent = `Image Size: Width: ${dimensions.width}, Height: ${dimensions.height}`;
        }
        // Note: Not calling updateSingleDefaultButtonState() here to avoid duplicate calls
        // It will be called by updateFileList() which renders the UI
    }
    updateDefaultButtonState() {
        depthDefaultSettings.updateDefaultButtonState(this);
    }
    updateSingleDefaultButtonState(fileIndex) {
        depthDefaultSettings.updateSingleDefaultButtonState(this, fileIndex);
    }
    async useAsDefaultSettings(fileIndex) {
        await depthDefaultSettings.useAsDefaultSettings(this, fileIndex);
    }
    async resetToDefaultSettings(fileIndex) {
        await depthDefaultSettings.resetToDefaultSettings(this, fileIndex);
    }
    resetMonoParameters(fileIndex) {
        depthDefaultSettings.resetMonoParameters(this, fileIndex);
    }
    resetDisparityOffset(fileIndex) {
        depthDefaultSettings.resetDisparityOffset(this, fileIndex);
    }
    resetPrinciplePoint(fileIndex) {
        depthDefaultSettings.resetPrinciplePoint(this, fileIndex);
    }
    async removeColorImageFromDepth(fileIndex) {
        await colorImageForDepth.removeColorImageFromDepth(this, fileIndex);
    }
    savePlyFile(fileIndex) {
        plyExport.savePlyFile(this, fileIndex);
    }
    generatePlyFileContent(spatialData, fileIndex) {
        return plyExport.generatePlyFileContent(this, spatialData, fileIndex);
    }
    // ========== Pose loading ==========
    async handlePoseData(message) {
        await pose.handlePoseData(this, message);
    }
    // ========== Camera Profile handling ==========
    handleCameraProfile(data, fileName) {
        cameraProfile.handleCameraProfile(this, data, fileName);
    }
    createCameraVisualization(cameraName, location, rotationQuaternion, rotationType) {
        return cameraProfile.createCameraVisualization(cameraName, location, rotationQuaternion, rotationType);
    }
    toggleCameraVisibility() {
        cameraProfile.toggleCameraVisibility(this);
    }
    updateCameraButtonState() {
        cameraProfile.updateCameraButtonState(this);
    }
    toggleCameraProfileLabels(cameraProfileIndex, showLabels) {
        cameraProfile.toggleCameraProfileLabels(this, cameraProfileIndex, showLabels);
    }
    toggleCameraProfileCoordinates(cameraProfileIndex, showCoords) {
        cameraProfile.toggleCameraProfileCoordinates(this, cameraProfileIndex, showCoords);
    }
    applyCameraScale(cameraProfileIndex, scale) {
        cameraProfile.applyCameraScale(this, cameraProfileIndex, scale);
    }
    handleMtlData(message) {
        formatDataHandlers.handleMtlData(this, message);
    }
    /**
     * Capture the current open/closed state of depth settings panels and form values
     */
    captureDepthPanelStates() {
        return depthPanelState.captureDepthPanelStates(this);
    }
    captureDepthFormValues(fileIndex) {
        return depthPanelState.captureDepthFormValues(this, fileIndex);
    }
    restoreDepthPanelStates(states) {
        depthPanelState.restoreDepthPanelStates(this, states);
    }
    restoreDepthFormValues(fileIndex, formValues) {
        depthPanelState.restoreDepthFormValues(this, fileIndex, formValues);
    }
    async promptForCameraParameters(fileName) {
        return depthCameraParamsPrompt.promptForCameraParameters(fileName);
    }
    async triggerDatasetCalibrationLoading(sceneMetadata) {
        await datasetWorkflow.triggerDatasetCalibrationLoading(this, sceneMetadata);
    }
    async triggerDatasetImageLoading(sceneMetadata) {
        await datasetWorkflow.triggerDatasetImageLoading(this, sceneMetadata);
    }
    async handleDatasetTexture(message) {
        await datasetWorkflow.handleDatasetTexture(this, message);
    }
}
// # VSCode changes: before this code was used instead of anything below
// Initialize when DOM is ready
// if (document.readyState === 'loading') {
//   document.addEventListener('DOMContentLoaded', () => new PointCloudVisualizer());
// } else {
//   new PointCloudVisualizer();
// }
// below, everything is new for the web version
// Export for global access
window.PointCloudVisualizer = PointCloudVisualizer;
// Test hook: container totals only arise from multi-scan E57/X3A loads driven by
// the extension host, so specs exercise the accumulator directly.
window.__plyContainerPerf = containerPerf;
// Test hook, same reason as __plyContainerPerf: the zero-copy PLY route runs
// only when the extension host hands the webview a URI to fetch, so a browser
// spec has no other way to reach it.
window.__plyParsePly = { parsePlyFromResponse, parsePlyWasm };
// Same reason: the NumPy reader's dtype and shape handling is worth testing
// directly, not only through a depth panel that shows one pixel of the result.
window.__plyNpy = { inspectNpyWasm, isNpyPointCloudShape, readNpyWasm };
// Same reason again: the depth kernels are reached through a camera-parameters
// dialog and a worker, neither of which a spec can use to check the arithmetic
// of a single pixel.
window.__plyDepth = {
    initTiffWasm,
    normalizeDepth,
    projectToPointCloud,
    // Both projection entry points, so a test can compare the banded path against
    // the single-pass one through the same API the pipeline uses rather than
    // reaching for the raw wasm global.
    projectDepthWasmSync,
    projectDepthBandWasmSync,
};
// The pool itself, so a benchmark can time it against the single pass.
window.__plyDepthPool = { projectDepthInBands };
// The picking workflow's live counters, so a test can assert what a real
// double-click did rather than calling the handler behind it.
window.__plyRegistrationState = registrationState;
// Test handle for the file-list state, following __plyContainerPerf above.
// Lets a browser test drive the background-work row without needing the
// extension host to stream real images.
window.__plyFilesState = filesState;
// Initialize when DOM is ready
let visualizer = null;
async function initializeVisualizer() {
    // Initialize themes first - only for browser version
    // VSCode handles theming natively via CSS variables
    if (!isVSCode) {
        await initializeThemes();
        console.log('✅ Theme system initialized');
        // Initialize theme switcher
        setupThemeSwitcher();
    }
    if (!visualizer) {
        visualizer = new PointCloudVisualizer();
        window.visualizer = visualizer;
        // Alongside the visualizer for the same reason: driving an alignment from
        // the console (or from a spec) needs the module's entry points, and it has
        // no other handle in the page.
        window.registrationFeature = registrationFeature;
        // Same reason: the station pipeline's result handler is only reachable
        // through the webview's message listener, which the standalone page never
        // installs, so a spec has no other way to drive it.
        window.stationPipelineFeature = stationPipelineFeature;
        // The colour-mode predicates decide how vertex colours are interpreted;
        // exposing them lets a spec assert the two photographic modes agree.
        window.__colorMode = colorModeModule;
        console.log('✅ PointCloudVisualizer initialized');
    }
}
function setupThemeSwitcher() {
    const themeSelector = document.getElementById('theme-selector');
    if (themeSelector) {
        // Set current theme as selected
        themeSelector.value = getCurrentThemeName();
        // Add event listener for theme changes
        themeSelector.addEventListener('change', async (event) => {
            const selectedTheme = event.target.value;
            console.log('🎨 Switching to theme:', selectedTheme);
            const theme = await getThemeByName(selectedTheme);
            if (theme) {
                applyTheme(theme);
                console.log('✅ Theme applied:', theme.displayName);
            }
            else {
                console.error('❌ Failed to load theme:', selectedTheme);
            }
        });
        console.log('✅ Theme switcher initialized');
    }
    else {
        console.warn('⚠️ Theme selector not found in DOM');
    }
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeVisualizer);
}
else {
    initializeVisualizer();
}
export default PointCloudVisualizer;
